<template>
  <div>
    <script type="application/javascript">
      fbq('track', 'ViewContent');
    </script>
    <div v-if="project.data && (!project.data.private || loggedIn)">
      <approval v-if="project.data && project.data.id" v-model="project.data" />
      <masonry :cols="{default: 3, 1300: 2, 700: 1}" :gutter="{default: '15px'}"
               class="masonry">
        <div>
          <project-info v-if="project.data" v-model="project.data" :editing="editing" :totalParticipantsPaid="totalParticipantsPaid"
                        @startEditing="startEditing" @refreshUpdates="$refs.update.fetchUpdates()" @updateDeadline="setDeadline" />
          <update ref="update" v-if="project.data" v-model="project.data" :editing="editing" :itemsTaken="items.taken" />
        </div>
        <div>
          <div class="card" v-if="project.data && (editing || ((paymentInfos.data && paymentInfos.data.length > 0 && ((items.taken && !registrationsFullyPaid) || (addons.taken && !addonsFullyPaid)))) && project.data.status !== 'CANCELLED')">
            <h1 class="card__title d-flex card__title__border">
              <div class="full-width">{{ $t('messages.payment_info') }}</div>
              <div v-if="editing">
                <b-button variant="primary" v-b-tooltip.hover :title="$t('messages.payment_info_new')" @click="initializePaymentInfo(null)">
                  <font-awesome-icon icon="plus"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div v-if="paymentInfos.data && paymentInfos.data.length === 0 && paymentInfos.name === null">
              <div class="card__addon__wrapper font-italic p-20">{{ $t('messages.payment_info_empty') }}</div>
            </div>
            <div v-if="(!editing || paymentInfos.name === null || this.paymentInfos.types.length === 0) && paymentInfos.data && paymentInfos.data.length > 0">
              <div class="card__info bg-warning font-weight-bold text-white" v-if="!editing && totalSumToBePaid > 0">
                <p>{{ $t('messages.total_paid_partly') }}</p>
                <p>{{ $t('messages.total_to_be_paid') }}: € {{ totalSumToBePaid | round(2) }}</p>
              </div>
              <div v-for="paymentInfo in paymentInfos.data" :key="paymentInfo.id" class="card__payment__info__wrapper card__payment__border__bottom">
                <div class="card__payment__info__icon">
                  <font-awesome-icon icon="money-check-alt" class="red" size="2x" v-if="paymentInfo.payment_type == 'IBAN'"></font-awesome-icon>
                  <font-awesome-icon :icon="['fab', 'paypal']" class="red" size="2x" v-if="paymentInfo.payment_type == 'PAYPAL'"></font-awesome-icon>
                </div>
                <div class="card__payment__info__part d-flex align-items-center">
                  <div class="full-width">
                    <p>{{ paymentInfo.name }}</p>
                    <p>{{ paymentInfo.description }}</p>
                    <p v-if="paymentInfo.payment_type === 'IBAN'">{{ paymentInfo.extra_info }}</p>
                    <p v-if="paymentInfo.payment_type === 'PAYPAL'">{{ $t('messages.payment_info_paypal') }}</p>
                  </div>
                  <div class="d-flex align-items-center card__payment__code" v-if="!editing && paymentInfo.payment_type == 'IBAN'">
                    <qrcode :value="qrCode(paymentInfo)" :options="{ width: 100 }"></qrcode>
                  </div>
                  <div v-if="!editing && paymentInfo.payment_type == 'PAYPAL' && paymentInfo.extra_info" class="card__payment__code">
                    <p><a :href="payPalMe(paymentInfo)" target="_blank"><font-awesome-icon :icon="['fab', 'cc-paypal']" size="4x"></font-awesome-icon></a></p>
                  </div>
                  <div class="d-flex align-items-center mr-m-15" v-if="editing">
                    <b-button v-b-tooltip.hover :title="$t('messages.payment_info_edit')" variant="transparent" @click="initializePaymentInfo(paymentInfo)" class="bg-transparent p-0" :class="paymentInfos.data.length > 1 ? 'mr-1' : ''" size="sm">
                      <font-awesome-icon icon="pencil-alt" class="red"></font-awesome-icon>
                    </b-button>
                    <click-confirm v-if="paymentInfos.data.length > 1" button-size="sm" :messages="confirmMessage">
                      <b-button v-b-tooltip.hover :title="$t('messages.payment_info_delete')" variant="transparent" @click="deletePaymentInfo(paymentInfo)" class="bg-transparent p-0 mt-m-1" size="sm">
                        <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                      </b-button>
                    </click-confirm>
                  </div>
                </div>
              </div>
              <div class="card__payment__info__wrapper">
                <p class="pt-10 pb-10">{{ $t('messages.payment_info_description') }}</p>
              </div>
            </div>
            <div class="card__description" v-if="editing && paymentInfos.name !== null && this.paymentInfos.types.length > 0">
              <b-form @submit.prevent="savePaymentInfo" class="pt-20">
                <b-form-group id="paymentInfoTypeGroup"
                              :label="$t('messages.type')+':'"
                              label-for="paymentInfoType"
                              :invalid-feedback="$t('errors.type')">
                  <b-form-select id="paymentInfoType" name="paymentInfoType"
                                 v-model="paymentInfos.type"
                                 @change="handlePaymentInfoSelectChange"
                                 :state="$v.paymentInfos.type.$dirty ? !$v.paymentInfos.type.$error : null">
                    <option :value="null"></option>
                    <option v-for="type in paymentInfos.types" :key="type.id" :value="type">{{ $t('messages.' + type.name.toLowerCase()) }}</option>
                  </b-form-select>
                </b-form-group>
                <b-form-group id="paymentInfoNameGroup"
                              :label="$t('messages.name')+':'"
                              label-for="paymentInfoName"
                              :invalid-feedback="$t('errors.name')">
                  <b-form-input id="paymentInfoName" name="paymentInfoName"
                                v-model="paymentInfos.name"
                                @input="$v.paymentInfos.name.$touch"
                                :state="$v.paymentInfos.name.$dirty ? !$v.paymentInfos.name.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="paymentInfoDescriptionGroup"
                              :label="(paymentInfos.type !== null && paymentInfos.type.name === 'PAYPAL' ? $t('messages.email') : $t('messages.account'))+':'"
                              label-for="paymentInfoDescription"
                              :invalid-feedback="paymentInfos.type !== null && paymentInfos.type.name === 'PAYPAL' ? $t('errors.email') : $t('errors.account')">
                  <b-form-input id="paymentInfoDescription" name="paymentInfoDescription"
                                v-model="paymentInfos.description"
                                @input="$v.paymentInfos.description.$touch"
                                :state="$v.paymentInfos.description.$dirty ? !$v.paymentInfos.description.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="paymentInfoExtraInfoGroup"
                              :label="(paymentInfos.type.name === 'PAYPAL') ? $t('messages.paypal_me') : $t('messages.bic') +':'"
                              label-for="paymentInfoExtraInfo"
                              v-if="paymentInfos.type !== null && (paymentInfos.type.name === 'IBAN' || paymentInfos.type.name === 'PAYPAL')">
                  <div class="d-flex align-items-center" v-if="paymentInfos.type.name === 'PAYPAL'">
                    <span class="mr-1">https://www.paypal.me/</span>
                    <b-form-input id="paymentInfoExtraInfo" name="paymentInfoExtraInfo"
                                v-model="paymentInfos.extraInfo"
                                @input="$v.paymentInfos.extraInfo.$touch"
                                :state="$v.paymentInfos.extraInfo.$dirty ? !$v.paymentInfos.extraInfo.$error : null"></b-form-input>
                  </div>
                  <b-form-input id="paymentInfoExtraInfo" name="paymentInfoExtraInfo"
                                v-model="paymentInfos.extraInfo"
                                @input="$v.paymentInfos.extraInfo.$touch"
                                :state="$v.paymentInfos.extraInfo.$dirty ? !$v.paymentInfos.extraInfo.$error : null"
                                v-if="paymentInfos.type.name === 'IBAN'"></b-form-input>
                </b-form-group>
                <div class="d-flex">
                  <notification-button :message="paymentInfos.id ? $t('messages.edit') : $t('messages.add')" :notification="notifications.paymentInfo" field="paymentInfo"
                                       :disabled="$v.paymentInfos.$invalid" class="mr-2"></notification-button>
                  <b-button variant="primary" @click="clearPaymentInfo">{{ $t('messages.cancel') }}</b-button>
                </div>
              </b-form>
            </div>
          </div>
          <div class="card" v-if="editing">
            <h1 class="card__title__border">{{ $t('messages.orders') }}</h1>
            <div v-if="(!items.data || items.data.length === 0) && (!addons.data || addons.data.length === 0)">
              <div class="card__addon__wrapper font-italic p-20">{{ $t('messages.orders_empty') }}</div>
            </div>
            <div v-if="items.data && items.data.length > 0">
              <div class="card__item__wrapper__part">
                <div class="card__item__part__wrapper card__item__border__bottom">
                  <div class="card__item__part pl-20 pr-20 font-weight-bold d-flex align-items-center">
                    {{ $t('messages.registrations') }}
                  </div>
                  <div class="card__item__part__order__info d-flex align-items-center justify-content-center">
                    {{ $t('messages.ordered') }}
                  </div>
                  <div class="card__item__part__order__info d-flex align-items-center justify-content-center">
                    {{ $t('messages.paid_partly') }}
                  </div>
                  <div class="card__item__part__order__info d-flex align-items-center justify-content-center">
                    {{ $t('messages.paid_fully') }}
                  </div>
                </div>
              </div>
            </div>
            <div v-for="item in items.data" :key="'oi' + item.id">
              <div class="card__item__wrapper__part">
                <div class="card__item__part__wrapper card__item__border__bottom">
                  <div class="card__item__part pl-20 pr-20 d-flex align-items-center">
                    {{ item.name }}
                  </div>
                  <div class="card__item__part__order__info">
                    {{ getTotalOrderedRegistrations(item) }}
                  </div>
                  <div class="card__item__part__order__info">
                    {{ getTotalPartlyPaidOrderedRegistrations(item) }}
                  </div>
                  <div class="card__item__part__order__info">
                    {{ getTotalPaidOrderedRegistrations(item) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="addons.data && addons.data.length > 0">
              <div class="card__addon__wrapper__part">
                <div class="card__addon__part__wrapper card__addon__border__bottom">
                  <div class="card__addon__part pl-20 pr-20 font-weight-bold d-flex align-items-center default-height">
                    {{ $t('messages.addons') }}
                  </div>
                  <div class="card__addon__part__order__info d-flex align-items-center justify-content-center default-height">
                    {{ $t('messages.ordered') }}
                  </div>
                  <div class="card__addon__part__order__info d-flex align-items-center justify-content-center default-height">
                    {{ $t('messages.paid_partly') }}
                  </div>
                  <div class="card__addon__part__order__info d-flex align-items-center justify-content-center default-height">
                    {{ $t('messages.paid_fully') }}
                  </div>
                </div>
              </div>
            </div>
            <div v-for="addon in addons.data" :key="'oa' + addon.id">
              <div class="card__addon__wrapper__part">
                <div class="card__addon__part__wrapper card__addon__border__bottom">
                  <div class="card__addon__part pl-20 pr-20 d-flex align-items-center default-height">
                    {{ addon.name }}
                  </div>
                  <div class="card__addon__part__order__info default-height">
                    {{ getTotalOrderedAddons(addon) }}
                  </div>
                  <div class="card__addon__part__order__info default-height">
                    {{ getTotalPartlyPaidOrderedAddons(addon) }}
                  </div>
                  <div class="card__addon__part__order__info default-height">
                    {{ getTotalPaidOrderedAddons(addon) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card" v-if="editing && participants.payment.payment === null">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.participants') }}</div>
            </h1>
            <div :class="participantsBackground">
              <div class="card__participant__wrapper__part">
                <div class="card__participant__border__bottom d-flex-responsive">
                  <div class="card__item__participant__part card__item__participant__part__manage d-flex card__participant__border__bottom-responsive align-items-center">
                    {{ $t('messages.total') }}
                  </div>
                  <div class="card__participant__payment">
                    <div class="d-flex">
                      <div class="card__participant__payment__part">
                        <div>{{ $t('messages.amount') }}:</div>
                        <div>€ {{ totalParticipantsAmount | round(2) }}</div>
                      </div>
                      <div class="card__participant__payment__part">
                        <div>{{ $t('messages.paid') }}:</div>
                        <div>€ {{ totalParticipantsPaid | round(2) }}</div>
                      </div>
                      <div class="card__participant__payment__part">
                        <div>{{ $t('messages.difference') }}:</div>
                        <div>€ {{ totalParticipantsDifference | round(2) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!participants.data || participants.data.length === 0">
              <div class="card__addon__wrapper font-italic p-20">{{ $t('messages.payments_empty') }}</div>
            </div>
            <div id="paymentScroll" class="card__scroll" :key="'pp' + participants.update">
              <div v-for="participant in participants.data" :key="participant.id">
                <div class="card__participant__wrapper__part">
                  <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getParticipantBackground(participant.items, participant.addons))">
                    <div class="card__item__participant__part card__item__participant__part__manage d-flex card__participant__border__bottom-responsive">
                      <div class="d-flex flex-wrap align-items-center full-width">
                        <div class="mr-2">
                          <font-awesome-icon v-if="!isSelectedParticipant(participant)" icon="chevron-circle-down" class="red pointer mr-2" @click="selectParticipant(participant)"></font-awesome-icon>
                          <font-awesome-icon v-if="isSelectedParticipant(participant)" icon="chevron-circle-up" class="red pointer mr-2" @click="selectParticipant(participant)"></font-awesome-icon>
                        </div>
                        <div>
                          <div class="mb-2"><b-link :to="{ path: '/user/' + slugText(participant.id + '-' + participant.full_name) }" class="text-inherit">{{ participant.full_name }}</b-link></div>
                          <div class="d-flex">
                            <click-confirm v-if="!participant.loadingUnsubscribe && getParticipantPaid(participant.items, participant.addons) === 0" button-size="sm" :messages="confirmMessage">
                              <b-button v-b-tooltip.hover :title="$t('messages.participants_unsubscribe')" variant="primary" @click="unsubscribe(participant)" class="mr-2">
                                <font-awesome-icon icon="user-slash"></font-awesome-icon>
                              </b-button>
                            </click-confirm>
                            <b-button v-if="participant.loadingUnsubscribe" variant="primary" class="mr-2" :disabled="true">
                              <font-awesome-icon icon="circle-notch" class="text-white" spin></font-awesome-icon>
                            </b-button>
                            <b-button v-b-tooltip.hover :title="$t('messages.participants_refund')" variant="primary" @click="preparePaymentParticipant(participant, true)" v-if="getParticipantPaid(participant.items, participant.addons) > 0" class="mr-2">
                              <font-awesome-icon icon="undo"></font-awesome-icon>
                            </b-button>
                            <b-button v-b-tooltip.hover :title="$t('messages.participants_payment')" variant="primary" @click="preparePaymentParticipant(participant, false)" v-if="getParticipantDifference(participant.items, participant.addons) < 0">
                              <font-awesome-icon icon="money-bill-wave"></font-awesome-icon>
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card__participant__payment d-flex align-items-center">
                      <div class="d-flex">
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.amount') }}:</div>
                          <div>€ {{ getParticipantAmount(participant.items, participant.addons) | round(2) }}</div>
                        </div>
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.paid') }}:</div>
                          <div>€ {{ getParticipantPaid(participant.items, participant.addons) | round(2) }}</div>
                        </div>
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.difference') }}:</div>
                          <div>€ {{ getParticipantDifference(participant.items, participant.addons) | round(2) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="participant.items && participant.items.length > 0 && isSelectedParticipant(participant)">
                    <div class="card__participant__border__bottom d-flex" :class="getBackgroundColor(getItemsBackground(participant.items))">
                      <div class="card__item__participant__part card__item__participant__part__manage d-flex flex-wrap align-items-center font-weight-bold">{{ $t('messages.registrations') }}</div>
                    </div>
                  </div>
                  <div v-if="isSelectedParticipant(participant)" v-for="item in participant.items" :key="'i' + item.id">
                    <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getItemBackground(item))">
                      <div class="card__item__participant__part card__item__participant__part__manage d-flex flex-wrap align-items-center card__participant__border__bottom-responsive">{{ item.amount }}x {{ item.name }}</div>
                      <div class="card__participant__payment">
                        <div class="d-flex">
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.amount') }}:</div>
                            <div>€ {{ (item.new_total_price * item.amount) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.paid') }}:</div>
                            <div>€ {{ item.paid | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.difference') }}:</div>
                            <div>€ {{ (item.paid - (item.new_total_price * item.amount)) | round(2) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="participant.addons && participant.addons.length > 0 && isSelectedParticipant(participant)" :class="getBackgroundColor(getAddonsBackground(participant.addons))">
                    <div class="card__participant__border__bottom d-flex">
                      <div class="card__addon__participant__part card__addon__participant__part__manage d-flex flex-wrap align-items-center font-weight-bold">{{ $t('messages.addons') }}</div>
                    </div>
                  </div>
                  <div v-if="isSelectedParticipant(participant)" v-for="addon in participant.addons" :key="'a' + addon.id">
                    <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getAddonBackground(addon))">
                      <div class="card__addon__participant__part card__addon__participant__part__manage d-flex flex-wrap align-items-center card__participant__border__bottom-responsive">{{ addon.amount }}x {{ addon.name }}</div>
                      <div class="card__participant__payment">
                        <div class="d-flex">
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.amount') }}:</div>
                            <div>€ {{ (addon.total_price * addon.amount) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.paid') }}:</div>
                            <div>€ {{ addon.paid | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.difference') }}:</div>
                            <div>€ {{ (addon.paid - (addon.total_price * addon.amount)) | round(2) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card" v-if="editing && participants.payment.payment !== null">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ participants.payment.refund ? $t('messages.refunds') : $t('messages.payments') }}</div>
              <div>
                <b-button v-b-tooltip.hover :title="participants.payment.paymentAdvanced ? $t('messages.participants_advanced_auto') : $t('messages.participants_advanced')" variant="primary" @click="switchPaymentAdvanced">
                  <font-awesome-icon icon="sliders-h"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div>
              <b-form @submit.prevent="savePaymentParticipant">
                <div class="card__participant__wrapper__part">
                  <div class="card__participant__border__bottom d-flex-responsive" v-if="!participants.payment.paymentAdvanced">
                    <div class="card__item__participant__part card__item__participant__part__manage d-flex flex-wrap align-items-center card__participant__border__bottom-responsive">
                      <div class="full-width">
                        <b-form-group id="participantPaymentAmountGroup"
                                      :label="$t('messages.amount')+' (€):'"
                                      label-for="participantPaymentAmount"
                                      :invalid-feedback="$t('errors.amount')">
                          <b-form-input id="participantPaymentAmount" name="participantPaymentAmount"
                                        ref="participantPaymentAmount"
                                        min="0" :number="true" type="number" step="0.01"
                                        v-model="paymentSum"
                                        @input="$v.paymentSum.$touch"
                                        :state="$v.paymentSum.$dirty ? !$v.paymentSum.$error : null"></b-form-input>
                        </b-form-group>
                      </div>
                    </div>
                  </div>
                  <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getParticipantBackground(participants.payment.paymentItems, participants.payment.paymentAddons))">
                    <div class="card__item__participant__part card__item__participant__part__manage d-flex flex-wrap align-items-center card__participant__border__bottom-responsive">
                      <div>{{ participants.payment.payment.full_name }}</div>
                    </div>
                    <div class="card__participant__payment">
                      <div class="d-flex">
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.amount') }}:</div>
                          <div>€ {{ getParticipantAmount(participants.payment.paymentItems, participants.payment.paymentAddons) | round(2) }}</div>
                        </div>
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.paid') }}:</div>
                          <div>€ {{ getParticipantPaid(participants.payment.paymentItems, participants.payment.paymentAddons) | round(2) }}</div>
                        </div>
                        <div class="card__participant__payment__part">
                          <div>{{ $t('messages.difference') }}:</div>
                          <div>€ {{ getParticipantDifference(participants.payment.paymentItems, participants.payment.paymentAddons) | round(2) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="participants.payment.paymentItems && participants.payment.paymentItems.length > 0">
                    <div class="card__participant__border__bottom d-flex" :class="getBackgroundColor(getItemsBackground(participants.payment.paymentItems))">
                      <div class="card__item__participant__part card__item__participant__part__manage d-flex flex-wrap align-items-center font-weight-bold">{{ $t('messages.registrations') }}</div>
                    </div>
                  </div>
                  <div v-for="(item, index) in participants.payment.paymentItems" :key="'i' + item.id">
                    <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getItemBackground(item))">
                      <div class="card__item__participant__part card__item__participant__part__manage d-flex card__participant__border__bottom-responsive">
                        <div class="d-flex flex-wrap align-items-center full-width">
                          <div>{{ item.amount }}x {{ item.name }}</div>
                        </div>
                        <div v-if="participants.payment.paymentAdvanced && ((!participants.payment.refund && (((item.new_total_price * item.amount) - item.paid) > 0)) || (participants.payment.refund && item.paid > 0))" class="d-flex">
                          <b-button v-b-tooltip.hover :title="item.selected ? $t('messages.participants_amount_delete') : $t('messages.participants_amount_insert')" variant="primary" @click="switchItemPayment(item)">
                            <font-awesome-icon :icon="item.selected ? 'times-circle' : (participants.payment.refund ? 'undo' : 'money-bill-wave')"></font-awesome-icon>
                          </b-button>
                        </div>
                      </div>
                      <div class="card__participant__payment">
                        <div class="d-flex">
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.amount') }}:</div>
                            <div>€ {{ (item.new_total_price * item.amount) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.paid') }}:</div>
                            <div>€ {{ (item.paid + (item.sum ? (parseFloat(item.sum) * (participants.payment.refund ? -1 : 1)) : 0)) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.difference') }}:</div>
                            <div>€ {{ ((item.paid + (item.sum ? (parseFloat(item.sum) * (participants.payment.refund ? -1 : 1)) : 0)) - (item.new_total_price * item.amount)) | round(2) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="participants.payment.paymentAdvanced && item.selected" class="p-20 card__participant__border__bottom">
                      <b-form-group id="itemPaymentAmountGroup"
                                    :label="$t('messages.amount')+' (€):'"
                                    label-for="itemPaymentAmount"
                                    :invalid-feedback="$t('errors.amount')">
                        <b-form-input id="itemPaymentAmount" name="itemPaymentAmount"
                                      min="0" :number="true" type="number" step="0.01"
                                      v-model="item.sum"
                                      @input="$v.participants.payment.paymentItems.$each[index].sum.$touch"
                                      :state="$v.participants.payment.paymentItems.$each[index].sum.$dirty ? !$v.participants.payment.paymentItems.$each[index].sum.$error : null"></b-form-input>
                      </b-form-group>
                    </div>
                  </div>
                  <div v-if="participants.payment.paymentAddons && participants.payment.paymentAddons.length > 0" :class="getBackgroundColor(getAddonsBackground(participants.payment.paymentAddons))">
                    <div class="card__participant__border__bottom d-flex">
                      <div class="card__addon__participant__part card__addon__participant__part__manage d-flex flex-wrap align-items-center font-weight-bold">{{ $t('messages.addons') }}</div>
                    </div>
                  </div>
                  <div v-for="(addon, index) in participants.payment.paymentAddons" :key="'a' + addon.id">
                    <div class="card__participant__border__bottom d-flex-responsive" :class="getBackgroundColor(getAddonBackground(addon))">
                      <div class="card__addon__participant__part card__addon__participant__part__manage d-flex card__participant__border__bottom-responsive">
                        <div class="d-flex flex-wrap align-items-center full-width">
                          <div>{{ addon.amount }}x {{ addon.name }}</div>
                        </div>
                        <div v-if="participants.payment.paymentAdvanced && ((!participants.payment.refund && (((addon.total_price * addon.amount) - addon.paid) > 0)) || (participants.payment.refund && addon.paid > 0))" class="d-flex">
                          <b-button v-b-tooltip.hover :title="addon.selected ? $t('messages.participants_amount_delete') : $t('messages.participants_amount_insert')" variant="primary" @click="switchAddonPayment(addon)">
                            <font-awesome-icon :icon="addon.selected ? 'times-circle' : (participants.payment.refund ? 'undo' : 'money-bill-wave')"></font-awesome-icon>
                          </b-button>
                        </div>
                      </div>
                      <div class="card__participant__payment">
                        <div class="d-flex">
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.amount') }}:</div>
                            <div>€ {{ (addon.total_price * addon.amount) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.paid') }}:</div>
                            <div>€ {{ (addon.paid + (addon.sum ? (parseFloat(addon.sum) * (participants.payment.refund ? -1 : 1)) : 0)) | round(2) }}</div>
                          </div>
                          <div class="card__participant__payment__part">
                            <div>{{ $t('messages.difference') }}:</div>
                            <div>€ {{ ((addon.paid + (addon.sum ? (parseFloat(addon.sum) * (participants.payment.refund ? -1 : 1)) : 0)) - (addon.total_price * addon.amount)) | round(2) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="participants.payment.paymentAdvanced && addon.selected" class="p-20 card__participant__border__bottom">
                      <b-form-group id="addonPaymentAmountGroup"
                                    :label="$t('messages.amount')+' (€):'"
                                    label-for="addonPaymentAmount"
                                    :invalid-feedback="$t('errors.amount')">
                        <b-form-input id="addonPaymentAmount" name="addonPaymentAmount"
                                      min="0" :number="true" type="number" step="0.01"
                                      v-model="addon.sum"
                                      @input="$v.participants.payment.paymentAddons.$each[index].sum.$touch"
                                      :state="$v.participants.payment.paymentAddons.$each[index].sum.$dirty ? !$v.participants.payment.paymentAddons.$each[index].sum.$error : null"></b-form-input>
                      </b-form-group>
                    </div>
                  </div>
                </div>
                <div class="d-flex p-20 card__participant__border__bottom" v-if="participants.payment.refund">
                  <div class="full-width">
                    <b-form-group id="refundTypeGroup"
                                  :label="$t('messages.reason')+':'"
                                  label-for="refundType">
                      <b-form-select id="refundType" name="refundType" v-model="participants.payment.refundType">
                        <option :value="{ type: 'refund', message: $t('messages.refund') }">{{ $t('messages.refund') }}</option>
                        <option :value="{ type: 'rectification', message: $t('messages.rectification') }">{{ $t('messages.rectification') }}</option>
                        <option :value="{ type: 'shipping_costs_clearance', message: $t('messages.shipping_costs_clearance') }">{{ $t('messages.shipping_costs_clearance') }}</option>
                        <option :value="{ type: 'other', message: null }">{{ $t('messages.other') }}</option>
                      </b-form-select>
                      <b-form-input id="paymentDescription" name="paymentDescription" v-model="participants.payment.paymentDescription"
                                    v-if="participants.payment.refundType !== null && participants.payment.refundType.type === 'other'"
                                    class="mt-2"></b-form-input>
                    </b-form-group>
                  </div>
                </div>
                <div class="d-flex p-20">
                  <div class="d-flex mr-auto">
                    <notification-button :message="$t('messages.save')" :notification="notifications.payment" field="payment"
                                         :disabled="$v.participants.payment.$invalid || $v.paymentSum.$invalid" class="mr-2"></notification-button>
                    <b-button variant="primary" @click="clearPaymentParticipant">{{ $t('messages.cancel') }}</b-button>
                  </div>
                  <b-form-group id="notifyParticipantGroup" class="m-0 mt-1">
                    <b-form-checkbox id="notifyParticipant" name="notifyParticipant" v-model="participants.payment.notify" class="checkboxLineHeight">{{ $t('messages.send_email') }}</b-form-checkbox>
                  </b-form-group>
                </div>
              </b-form>
            </div>
          </div>
          <div class="card" v-if="project.data && (editing || (items.data && items.data.length > 0))">
            <h1 class="card__title__border d-flex">
              <div class="full-width"><div v-if="!editing">{{ $t('messages.signing_up') }}</div><div v-if="editing">{{ $t('messages.registrations') }}</div></div>
              <div v-if="editing">
                <b-button v-b-tooltip.hover :title="$t('messages.signing_up_new')" variant="primary" @click="initializeItem(null)">
                  <font-awesome-icon icon="plus"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div v-if="!editing">
              <div class="card__info bg-danger font-weight-bold text-white" v-if="project.deadline || project.data.status !== 'OPEN' || !loggedIn">
                <p v-if="project.data.status === 'CANCELLED'">{{ $t('messages.signing_up_cancelled') }}</p>
                <p v-if="project.deadline && project.data.status !== 'CANCELLED'">{{ $t('messages.signing_up_deadline') }}</p>
                <p v-if="project.deadline && project.data.status !== 'CANCELLED' && items.taken && !project.data.distribution_locked">{{ $t('messages.signing_up_pickup') }}</p>
                <p v-if="!project.deadline && project.data.status === 'CLOSED'">{{ $t('messages.signing_up_closed') }}</p>
                <p v-if="!project.deadline && project.data.status === 'OPEN' && !loggedIn">{{ $t('messages.signing_up_login') }}</p>
              </div>
              <div class="card__info bg-success font-weight-bold text-white" v-if="registrationsFullyPaid && project.data.status !== 'CANCELLED'">
                <p>{{ $t('messages.signing_up_paid_fully') }}</p>
              </div>
              <div class="card__info bg-warning font-weight-bold text-white" v-if="registrationsPartlyPaid && registrationsSumToBePaid > 0 && project.data.status !== 'CANCELLED'">
                <p>{{ $t('messages.signing_up_paid_partly') }}</p>
                <p>{{ $t('messages.signing_up_to_be_paid') }}: € {{ registrationsSumToBePaid | round(2) }}</p>
              </div>
              <div class="card__info font-weight-bold text-secondary" v-if="!editing && project.data.store">
                <p>{{ $t('messages.store_info') }}</p>
              </div>
              <b-form @submit.prevent="signup">
                <div v-for="(item, index) in items.data" :key="item.id" class="card__item__wrapper" :class="{ 'bg-odd': index % 2 == 1 }">
                  <div class="d-flex justify-content-center">
                    <div class="card__item">
                      <b-form-checkbox v-model="items.selectedTemp[index]" :value="item" :disabled="signupDisabled(item)" @change="handleItemChange(index, item)" />
                      <b-form-input v-model="item.amount" :number="true" type="number" :min="0" :max="getOldItemAmount(item) + item.max_participants - item.current_participants"
                                    :disabled="signupAmountDisabled(item) || !items.selectedTemp[index]"
                                    @input="handleItemInputChange(item)" @blur="handleItemBlurChange(item)" :key="items.update" />
                    </div>
                  </div>
                  <div class="card__item__wrapper__part">
                    <div :class="item.selected ? getBackgroundColor(getItemBackground(getOldItem(item))) : getBackgroundColor('')" class="card__item__part__wrapper card__item__border__bottom">
                      <div class="card__item__part">
                        {{ item.name }}
                      </div>
                      <div class="card__item__part__info d-flex justify-content-center align-items-center">
                        <del>€ {{ item.normal_total_price | round(2) }}</del>
                        <b-button v-b-tooltip.hover :title="getCost(item.normal_price, item.normal_shipping, item.normal_shipping_known)"
                                  class="info_button_margin noPadding noBackground"><font-awesome-icon icon="info-circle" class="red"></font-awesome-icon></b-button>
                      </div>
                      <div class="card__item__part__info d-flex justify-content-center align-items-center">
                        € {{ item.new_total_price | round(2) }}
                        <b-button v-b-tooltip.hover :title="getCost(item.new_price, item.new_shipping, item.new_shipping_known)"
                                  class="info_button_margin noPadding noBackground"><font-awesome-icon icon="info-circle" class="red"></font-awesome-icon></b-button>
                      </div>
                    </div>
                    <div class="card__item__part__wrapper card__item__border__bottom">
                      <div v-if="isItemStillJoinable(item)" :class="cardItemBackground(item)" class="card__item__part font-weight-bold text-white">
                        {{ item.max_participants - item.current_participants }} {{ (item.max_participants - item.current_participants) > 1 ? $t('messages.spots_free') : $t('messages.spots_free_one') }}
                      </div>
                      <div v-if="isItemFull(item)" :class="cardItemBackground(item)" class="card__item__part font-weight-bold text-white">
                        {{ $t('messages.sorry_full') }}
                      </div>
                      <div v-if="!isItemSet(item)" :class="cardItemBackground(item)" class="card__item__part font-weight-bold text-white">
                        {{ item.min_participants - item.current_participants }} {{ (item.min_participants - item.current_participants) > 1 ? $t('messages.participants_needed') : $t('messages.participants_needed_one') }}
                      </div>
                      <div :class="cardItemBackground(item)" class="card__item__part__info text-white">
                        Min: {{ item.min_participants }}
                      </div>
                      <div :class="cardItemBackground(item)" class="card__item__part__info text-white">
                        Max: {{ item.max_participants }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card__pickup__send" v-if="!project.data.organisator">
                  <div class="float-left pt-2">
                    {{ $t('messages.pickup_send') }}:
                  </div>
                  <div class="float-left ml-3">
                    <b-form-select v-model="items.pickup" :disabled="!loggedIn || project.data.distribution_locked || (!this.items.taken && this.project.deadline)">
                      <option v-if="project.data.allow_shipping || project.data.pickup_type === 'send'" :value="{ type: 'send', id: null }">{{ $t('messages.send_ship') }}</option>
                      <optgroup :label="$t('messages.pickup')">
                        <option v-for="pickupPoint in participants.pickupPoints" :key="'p-' + pickupPoint.id" :value="{ type: 'pickup', id: pickupPoint.id }" :disabled="isOwnPickupPoint(pickupPoint)">
                          {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}{{ pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '' }} ({{ pickupPoint.full_name }})
                        </option>
                      </optgroup>
                      <optgroup :label="$t('messages.pickup_by')" v-if="participants.pickupBy && participants.pickupBy.length > 0">
                        <option v-for="participantPickupBy in participants.pickupBy" :key="'u-' + participantPickupBy.id"
                                :value="{ type: 'user', id: participantPickupBy.id }" :disabled="participantPickupBy.id == $store.state.userId">{{ participantPickupBy.full_name }}</option>
                      </optgroup>
                    </b-form-select>
                  </div>
                  <div class="clear"></div>
                </div>
                <div class="card__bottom" v-if="!(this.items.taken && this.project.deadline && this.project.data.organisator)">
                  <notification-button :message="signupMessage" :notification="notifications.registration" field="registration"
                                       :disabled="signupButtonDisabled"></notification-button>
                </div>
              </b-form>
            </div>
            <div v-if="editing && items.name === null">
              <div v-if="!items.data || items.data.length === 0">
                <div class="card__addon__wrapper font-italic p-20">{{ $t('messages.signing_up_empty') }}</div>
              </div>
              <div v-for="item in items.data" :key="item.id" class="card__item__wrapper">
                <div class="d-flex full-width">
                  <div class="full-width">
                    <div class="card__item__part__wrapper card__item__border__bottom">
                      <div class="card__item__part card__item__part__manage">
                        {{ item.name }}
                      </div>
                      <div class="card__item__part__info">
                        <del>€ {{ item.normal_total_price | round(2) }}</del>
                      </div>
                      <div class="card__item__part__info">
                        € {{ item.new_total_price | round(2) }}
                      </div>
                    </div>
                    <div class="card__item__part__wrapper card__item__border__bottom">
                      <div class="card__item__part card__item__part__manage font-weight-bold">
                        <div v-if="item.current_participants > 0" class="red">
                          {{ $t('errors.item_used') }}
                        </div>
                      </div>
                      <div class="card__item__part__info">
                        Min: {{ item.min_participants }}
                      </div>
                      <div class="card__item__part__info">
                        Max: {{ item.max_participants }}
                      </div>
                    </div>
                  </div>
                  <div class="card__item__part__info card__item__border__bottom d-flex justify-content-center align-items-center manage_width">
                    <b-button v-b-tooltip.hover :title="$t('messages.signing_up_edit')" variant="transparent" @click="initializeItem(item)" class="bg-transparent p-0" :class="item.current_participants === 0 ? 'mr-1' : ''" size="sm">
                      <font-awesome-icon icon="pencil-alt" class="red"></font-awesome-icon>
                    </b-button>
                    <click-confirm v-if="item.current_participants === 0" button-size="sm" :messages="confirmMessage">
                      <b-button v-b-tooltip.hover :title="$t('messages.signing_up_delete')" variant="transparent" @click="deleteItem(item)" class="bg-transparent p-0 mt-m-1" size="sm">
                        <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                      </b-button>
                    </click-confirm>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="editing && items.name !== null" class="card__description pt-20">
              <b-form @submit.prevent="saveItem">
                <b-form-group id="itemNameGroup"
                              :label="$t('messages.name_product')+':'"
                              label-for="itemName"
                              :invalid-feedback="$t('errors.name_product')">
                  <b-form-input id="itemName" name="itemName"
                                v-model="items.name"
                                @input="$v.items.name.$touch"
                                :state="$v.items.name.$dirty ? !$v.items.name.$error : null"
                                :disabled="itemsParticipant"></b-form-input>
                </b-form-group>
                <b-form-group id="itemNormalPriceGroup"
                              :label="$t('messages.normal_price')+' (€):'"
                              label-for="itemNormalPrice"
                              :invalid-feedback="!$v.items.normalPrice.decimalTwoRounds ? $t('errors.normal_price_valid') : $t('errors.normal_price')">
                  <b-form-input id="itemNormalPrice" name="itemNormalPrice"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="items.normalPrice"
                                @input="$v.items.normalPrice.$touch"
                                :state="$v.items.normalPrice.$dirty ? !$v.items.normalPrice.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="itemNewPriceGroup"
                              :label="$t('messages.new_price')+' (€):'"
                              label-for="itemNewPrice"
                              :invalid-feedback="newPriceError">
                  <b-form-input id="itemNewPrice" name="itemNewPrice"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="items.newPrice"
                                @input="$v.items.newPrice.$touch"
                                :state="$v.items.newPrice.$dirty ? !$v.items.newPrice.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="itemNormalShippingKnownGroup"
                              :label="$t('messages.normal_shipping_known')+':'"
                              label-for="itemNormalShippingKnown">
                  <b-form-checkbox id="itemNormalShippingKnown" name="itemNormalShippingKnown"
                                   v-model="items.normalShippingKnown" class="fix-shipping-checkbox"></b-form-checkbox>
                </b-form-group>
                <b-form-group id="itemNormalShippingGroup"
                              :label="$t('messages.normal_shipping')+' (€):'"
                              label-for="itemNormalShipping"
                              :invalid-feedback="!$v.items.normalShipping.decimalTwoRounds ? $t('errors.normal_shipping_valid') : $t('errors.normal_shipping')"
                              v-if="!!items.normalShippingKnown">
                  <b-form-input id="itemNormalShipping" name="itemNormalShipping"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="items.normalShipping"
                                @input="$v.items.normalShipping.$touch"
                                :state="$v.items.normalShipping.$dirty ? !$v.items.normalShipping.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="itemNewShippingKnownGroup"
                              :label="$t('messages.new_shipping_known')+':'"
                              label-for="itemNewShippingKnown">
                  <b-form-checkbox id="itemNewShippingKnown" name="itemNewShippingKnown"
                                   v-model="items.newShippingKnown" class="fix-shipping-checkbox"></b-form-checkbox>
                </b-form-group>
                <b-form-group id="itemNewShippingGroup"
                              :label="$t('messages.new_shipping')+' (€):'"
                              label-for="itemNewShipping"
                              :invalid-feedback="!$v.items.newShipping.decimalTwoRounds ? $t('errors.new_shipping_valid') : $t('errors.new_shipping')"
                              v-if="!!items.newShippingKnown">
                  <b-form-input id="itemNewShipping" name="itemNewShipping"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="items.newShipping"
                                @input="$v.items.newShipping.$touch"
                                :state="$v.items.newShipping.$dirty ? !$v.items.newShipping.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="itemCurrentParticipantsGroup" v-if="items.id"
                              :label="$t('messages.current_participants')+':'">
                  {{ items.currentParticipants }}
                </b-form-group>
                <b-form-group id="itemMinParticipantsGroup"
                              :label="$t('messages.min_participants')+':'"
                              label-for="itemMinParticipants"
                              :invalid-feedback="minParticipantsError">
                  <b-form-input id="itemMinParticipants" name="itemMinParticipants"
                                min="0" :number="true" type="number"
                                v-model="items.minParticipants"
                                @input="$v.items.minParticipants.$touch"
                                :state="$v.items.minParticipants.$dirty ? !$v.items.minParticipants.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="itemMaxParticipantsGroup"
                              :label="$t('messages.max_participants')+':'"
                              label-for="itemMaxParticipants"
                              :invalid-feedback="maxParticipantsError">
                  <b-form-input id="itemMaxParticipants" name="itemMaxParticipants"
                                min="0" :number="true" type="number"
                                v-model="items.maxParticipants"
                                @input="$v.items.maxParticipants.$touch"
                                :state="$v.items.maxParticipants.$dirty ? !$v.items.maxParticipants.$error : null"></b-form-input>
                </b-form-group>
                <div class="d-flex">
                  <div class="d-flex mr-auto">
                    <notification-button :message="items.id ? $t('messages.edit') : $t('messages.add')" :notification="notifications.registration" field="registration"
                                         :disabled="$v.items.$invalid" class="mr-2"></notification-button>
                    <b-button variant="primary" @click="clearItem">{{ $t('messages.cancel') }}</b-button>
                  </div>
                  <b-form-group id="notifyItemGroup" class="m-0 mt-1">
                    <b-form-checkbox id="notifyItem" name="notifyItem" v-model="items.notify" class="checkboxLineHeight">{{ $t('messages.send_email') }}</b-form-checkbox>
                  </b-form-group>
                </div>
              </b-form>
            </div>
          </div>
          <div class="card" v-if="project.data && (editing || (addons.data && addons.data.length > 0))">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.addons') }}</div>
              <click-confirm v-if="editing && !project.data.addons_locked" button-size="sm" :messages="confirmMessage">
                <div>
                  <b-button v-b-tooltip.hover :title="$t('messages.addons_lock')" variant="primary" @click="lockAddons" class="mr-2">
                    <font-awesome-icon icon="lock"></font-awesome-icon>
                  </b-button>
                </div>
              </click-confirm>
              <click-confirm v-if="editing && project.data.addons_locked" button-size="sm" :messages="confirmMessage">
                <div>
                  <b-button v-b-tooltip.hover :title="$t('messages.addons_lock_open')" variant="primary" @click="unlockAddons" class="mr-2">
                    <font-awesome-icon icon="unlock"></font-awesome-icon>
                  </b-button>
                </div>
              </click-confirm>
              <div v-if="editing">
                <b-button v-b-tooltip.hover :title="$t('messages.addons_new')" variant="primary" @click="initializeAddon(null)">
                  <font-awesome-icon icon="plus"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div v-if="!editing">
              <div class="card__info bg-danger font-weight-bold text-white" v-if="(project.data.addons_locked || !loggedIn) && project.data.status !== 'CANCELLED'">
                <p v-if="project.data.addons_locked">{{ $t('messages.addons_locked') }}</p>
                <p v-if="!project.data.addons_locked && !loggedIn">{{ $t('messages.addons_login') }}</p>
              </div>
              <div class="card__info bg-success font-weight-bold text-white" v-if="addonsFullyPaid && project.data.status !== 'CANCELLED'">
                <p>{{ $t('messages.addons_paid_fully') }}</p>
              </div>
              <div class="card__info bg-warning font-weight-bold text-white" v-if="addonsPartlyPaid && project.data.status !== 'CANCELLED'">
                <p>{{ $t('messages.addons_paid_partly') }}</p>
                <p>{{ $t('messages.addons_to_be_paid') }}: € {{ addonsSumToBePaid | round(2) }}</p>
              </div>
              <div class="card__info font-weight-bold text-secondary" v-if="!editing && project.data.store">
                <p>{{ $t('messages.store_info') }}</p>
              </div>
              <b-form @submit.prevent="saveAddons">
                <div v-for="(addon, index) in addons.data" :key="addon.id" class="card__addon__wrapper" :class="{ 'bg-odd': index % 2 == 1 }">
                  <div class="d-flex justify-content-center">
                    <div class="card__addon">
                      <b-form-checkbox v-model="addons.selectedTemp[index]" :value="addon" :disabled="addonDisabled(addon)" @change="handleAddonChange(index, addon)" />
                      <b-form-input v-model="addon.amount" :number="true" type="number" min="0" :disabled="addonAmountDisabled(addon) || !addons.selectedTemp[index]" />
                    </div>
                  </div>
                  <div class="card__addon__wrapper__part">
                    <div :class="addon.selected ? getBackgroundColor(getAddonBackground(getOldAddon(addon))) : getBackgroundColor('')" class="card__addon__part__wrapper card__addon__border__bottom">
                      <div class="card__addon__part">
                        {{ addon.name }}
                      </div>
                      <div class="card__addon__part__info d-flex justify-content-center align-items-center">
                        € {{ addon.total_price | round(2) }}
                        <b-button v-b-tooltip.hover :title="getCost(addon.price, addon.shipping, addon.shipping_known)"
                                  class="info_button_margin noPadding noBackground"><font-awesome-icon icon="info-circle" class="red"></font-awesome-icon></b-button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card__bottom">
                  <notification-button :message="addonMessage" :notification="notifications.addon" field="addon"
                                       :disabled="addonButtonDisabled"></notification-button>
                </div>
              </b-form>
            </div>
            <div v-if="editing && addons.name === null && addons.data && addons.data.length > 0">
              <div v-for="addon in addons.data" :key="addon.id" class="card__addon__wrapper">
                <div class="d-flex full-width">
                  <div class="full-width">
                    <div class="card__addon__wrapper__part card__addon__border__bottom">
                      <div class="card__addon__part__wrapper">
                        <div class="card__addon__part card__addon__part__manage flex-wrap">
                          <p>{{ addon.name }}</p>
                          <p v-if="addon.current_participants > 0" class="red font-weight-bold">
                            {{ $t('errors.addon_used') }}
                          </p>
                        </div>
                        <div class="card__addon__part__info">
                          € {{ addon.total_price | round(2) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card__addon__border__bottom">
                    <div class="card__addon__part__info d-flex justify-content-center align-items-center manage_width full-height">
                      <b-button v-b-tooltip.hover :title="$t('messages.addons_edit_admin')" variant="transparent" @click="initializeAddon(addon)" class="bg-transparent p-0" :class="addon.current_participants === 0 ? 'mr-1' : ''" size="sm">
                        <font-awesome-icon icon="pencil-alt" class="red"></font-awesome-icon>
                      </b-button>
                      <click-confirm v-if="addon.current_participants === 0" button-size="sm" :messages="confirmMessage">
                        <b-button v-b-tooltip.hover :title="$t('messages.addons_delete')" variant="transparent" @click="deleteAddon(addon)" class="bg-transparent p-0 mt-m-1" size="sm">
                          <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                        </b-button>
                      </click-confirm>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="editing && addons.name === null && addons.data && addons.data.length === 0">
              <div class="card__addon__wrapper font-italic p-20">{{ $t('messages.addons_empty') }}</div>
            </div>
            <div v-if="editing && addons.name !== null" class="card__description pt-20">
              <b-form @submit.prevent="saveAddon">
                <b-form-group id="addonNameGroup"
                              :label="$t('messages.name_product')+':'"
                              label-for="addonName"
                              :invalid-feedback="$t('errors.name_product')">
                  <b-form-input id="addonName" name="addonName"
                                v-model="addons.name"
                                @input="$v.addons.name.$touch"
                                :state="$v.addons.name.$dirty ? !$v.addons.name.$error : null"
                                :disabled="addonsParticipant"></b-form-input>
                </b-form-group>
                <b-form-group id="addonPriceGroup"
                              :label="$t('messages.price')+' (€):'"
                              label-for="addonPrice"
                              :invalid-feedback="!$v.addons.price.decimalTwoRounds ? $t('errors.price_valid') : $t('errors.price')">
                  <b-form-input id="addonPrice" name="addonPrice"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="addons.price"
                                @input="$v.addons.price.$touch"
                                :state="$v.addons.price.$dirty ? !$v.addons.price.$error : null"></b-form-input>
                </b-form-group>
                <b-form-group id="addonShippingKnownGroup"
                              :label="$t('messages.shipping_price_known')+':'"
                              label-for="addonShippingKnown">
                  <b-form-checkbox id="addonShippingKnown" name="addonShippingKnown"
                                   v-model="addons.shippingKnown" class="fix-shipping-checkbox"></b-form-checkbox>
                </b-form-group>
                <b-form-group id="addonShippingGroup"
                              :label="$t('messages.shipping_price')+' (€):'"
                              label-for="addonShipping"
                              :invalid-feedback="!$v.addons.shipping.decimalTwoRounds ? $t('errors.shipping_valid') : $t('errors.shipping')"
                              v-if="!!addons.shippingKnown">
                  <b-form-input id="addonShipping" name="addonShipping"
                                min="0" :number="true" type="number" step="0.01"
                                v-model="addons.shipping"
                                @input="$v.addons.shipping.$touch"
                                :state="$v.addons.shipping.$dirty ? !$v.addons.shipping.$error : null"></b-form-input>
                </b-form-group>
                <div class="d-flex">
                  <div class="d-flex mr-auto">
                    <notification-button :message="addons.id ? $t('messages.edit') : $t('messages.add')" :notification="notifications.addon" field="addon"
                                         :disabled="$v.addons.$invalid" class="mr-2"></notification-button>
                    <b-button variant="primary" @click="clearAddon">{{ $t('messages.cancel') }}</b-button>
                  </div>
                  <b-form-group id="notifyAddonGroup" class="m-0 mt-1">
                    <b-form-checkbox id="notifyAddon" name="notifyAddon" v-model="addons.notify" class="checkboxLineHeight">{{ $t('messages.send_email') }}</b-form-checkbox>
                  </b-form-group>
                </div>
              </b-form>
            </div>
          </div>
        </div>
        <div>
          <div class="card" v-if="project.data && participants.pickupPoints && participants.pickupPoints.length > 0 && !editing">
            <h1 class="card__title">{{ $t('messages.map') }}</h1>
            <div class="card__description">
              <div class="float-left"><img :src="pickupPointIcon" :title="pickupPointMessage" /> &nbsp;{{ $t('messages.pickup') }} &nbsp;</div>
              <div class="float-left"><img :src="participantIcon" :title="participantMessage" /> &nbsp;{{ $t('messages.participant') }}</div>
              <div class="clear"></div>
              <div id="projectMap"></div>
            </div>
          </div>
          <div class="card" v-if="editing && project.data && participants.pickupPoints">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.pickup_points') }}</div>
              <div>
                <b-button v-b-tooltip.hover :title="$t('messages.pickup_points_new')" variant="primary" @click="initializePickupPoint(null)">
                  <font-awesome-icon icon="plus"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div class="card__scroll" v-if="participants.pickup.pickupPointParticipant === null && participants.pickupPoints && participants.pickupPoints.length > 0">
              <div v-for="pickupPoint in participants.pickupPoints" :key="pickupPoint.id" class="card__pickup__point__wrapper">
                <div class="d-flex full-width">
                  <div class="full-width">
                    <div class="card__pickup__point__wrapper__part card__pickup__point__border__bottom">
                      <div class="card__pickup__point__part__wrapper">
                        <div class="card__pickup__point__part card__pickup__point__part__manage flex-wrap">
                          {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}{{ pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '' }}
                        </div>
                        <div class="card__pickup__point__part__info">
                          {{ pickupPoint.full_name }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card__pickup__point__border__bottom">
                    <div class="card__pickup__point__part__info d-flex justify-content-center align-items-center manage_width full-height">
                      <b-button v-b-tooltip.hover :title="$t('messages.pickup_points_edit')" variant="transparent" @click="initializePickupPoint(pickupPoint)"
                                class="bg-transparent p-0 mr-1" size="sm">
                        <font-awesome-icon icon="pencil-alt" class="red"></font-awesome-icon>
                      </b-button>
                      <click-confirm button-size="sm" :messages="confirmMessage" v-if="!participants.pickup.pickupPointDeleting && !project.data.distribution_locked">
                        <b-button v-b-tooltip.hover :title="$t('messages.pickup_points_delete')" variant="transparent" @click="deletePickupPoint(pickupPoint)" class="bg-transparent p-0 mt-m-1" size="sm">
                          <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                        </b-button>
                      </click-confirm>
                      <b-button v-if="participants.pickup.pickupPointDeleting || project.data.distribution_locked" v-b-tooltip.hover :title="$t('messages.pickup_points_delete')"
                                variant="transparent" class="bg-transparent p-0" size="sm" :disabled="true">
                        <font-awesome-icon icon="trash-alt" class="red"></font-awesome-icon>
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="editing && participants.pickup.pickupPointParticipant === null && participants.pickupPoints && participants.pickupPoints.length === 0">
              <div class="card__pickup__point__wrapper font-italic p-20">{{ $t('messages.pickup_points_empty') }}</div>
            </div>
            <div v-if="editing && participants.pickup.pickupPointParticipant !== null" class="card__description pt-20">
              <b-form @submit.prevent="savePickupPoint">
                <b-form-group id="pickupPointNameGroup"
                              :label="$t('messages.name')+':'"
                              label-for="pickupPointName"
                              :invalid-feedback="$t('errors.name')">
                  <b-form-select v-model="participants.pickup.pickupPointParticipant"
                                 @change="$v.participants.pickup.pickupPointParticipant.$touch"
                                 :state="$v.participants.pickup.pickupPointParticipant.$dirty ? !$v.participants.pickup.pickupPointParticipant.$error : null">
                    <option v-for="participant in pickupPointParticipants" :key="'p-' + participant.id" :value="participant">
                      {{ participant.full_name }}
                    </option>
                  </b-form-select>
                </b-form-group>
                <b-form-group id="pickupPointLocationGroup"
                              v-bind:label="$t('messages.location')+':'"
                              label-for="pickupPointLocation"
                              :invalid-feedback="$t('errors.location')"
                              v-if="!participants.pickup.pickupPointId">
                  <location-picker id="pickupPointLocation" name="pickupPointLocation"
                                   :setCityAndCountry="setCityAndCountry"
                                   :isValidState="($v.participants.pickup.pickupPointCity.$dirty || $v.participants.pickup.pickupPointCountry.$dirty) ? (!$v.participants.pickup.pickupPointCity.$error && !$v.participants.pickup.pickupPointCountry.$error) : null"></location-picker>
                </b-form-group>
                <b-form-group id="pickupPointLocationEditGroup"
                              :label="$t('messages.location')+':'"
                              label-for="pickupPointLocationEdit"
                              v-if="!!participants.pickup.pickupPointId">
                  <b-form-input id="pickupPointLocationEdit" name="pickupPointLocationEdit"
                                :disabled="true" :placeholder="participants.pickup.pickupPointCity + ', ' + $i18n.t('countries.' + participants.pickup.pickupPointCountry)"></b-form-input>
                </b-form-group>
                <b-form-group id="pickupPointExtraInfoGroup"
                              :label="$t('messages.extra_info')+':'"
                              label-for="pickupPointExtraInfo">
                  <b-form-input id="pickupPointExtraInfo" name="pickupPointExtraInfo"
                                v-model="participants.pickup.pickupPointExtraInfo" :disabled="!!participants.pickup.pickupPointId"></b-form-input>
                </b-form-group>
                <div class="d-flex">
                  <notification-button :message="participants.pickup.pickupPointId ? $t('messages.edit') : $t('messages.add')" :notification="notifications.pickupPoint" field="pickupPoint"
                                       :disabled="$v.participants.pickup.$invalid" class="mr-2"></notification-button>
                  <b-button variant="primary" @click="clearPickupPoint">{{ $t('messages.cancel') }}</b-button>
                </div>
              </b-form>
            </div>
          </div>
          <div class="card" v-if="loggedIn && editing && project.data && distribution.loose && distribution.loose.length > 0">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.distribution_loose') }}</div>
            </h1>
            <div class="card__scroll" :key="'pl' + distribution.update">
              <div v-for="participant in distribution.loose" :key="'dl' + participant.id" class="card__participant__wrapper">
                <div class="d-flex full-width">
                  <div class="full-width">
                    <div class="card__participant__wrapper__part card__participant__border__bottom">
                      <div class="card__participant__part__wrapper">
                        <div class="card__participant__part card__participant__part__manage flex-wrap">
                          <p><b-link :to="{ path: '/user/' + slugText(participant.id + '-' + participant.full_name) }" class="text-inherit">{{ participant.full_name }}</b-link></p>
                          <div class="d-flex align-items-center mt-2">
                            <b-form-select v-model="participant.pickup" class="mr-2">
                              <option :value="{ type: 'send', id: null }">{{ $t('messages.send_ship') }}</option>
                              <optgroup :label="$t('messages.pickup')">
                                <option v-for="pickupPoint in participants.pickupPoints" :key="'p-' + pickupPoint.id" :value="{ type: 'pickup', id: pickupPoint.id }" :disabled="isOwnPickupPointParticipant(pickupPoint, participant.id)">
                                  {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}{{ pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '' }}
                                </option>
                              </optgroup>
                              <optgroup :label="$t('messages.pickup_by')" v-if="participants.pickupBy && participants.pickupBy.length > 0">
                                <option v-for="participantPickupBy in participants.pickupBy" :key="'u-' + participantPickupBy.id"
                                        :value="{ type: 'user', id: participantPickupBy.id }" :disabled="participantPickupBy.id == participant.id">{{ participantPickupBy.full_name }}</option>
                              </optgroup>
                            </b-form-select>
                            <b-button v-if="!participant.loadingPickup" v-b-tooltip.hover :title="$t('messages.distribution_edit')" variant="primary" size="sm">
                              <font-awesome-icon icon="pencil-alt" class="text-white" @click="pickupParticipant(participant)"></font-awesome-icon>
                            </b-button>
                            <b-button v-if="participant.loadingPickup" v-b-tooltip.hover :title="$t('messages.distribution_edit')" variant="primary" size="sm" :disabled="true">
                              <font-awesome-icon icon="circle-notch" class="text-white" spin></font-awesome-icon>
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card" v-if="loggedIn && project.data && distribution.data.pickups">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.distribution') }}</div>
              <div class="d-flex">
                <click-confirm v-if="editing && !project.data.distribution_locked" button-size="sm" :messages="confirmMessage">
                  <div>
                    <b-button v-b-tooltip.hover :title="$t('messages.distribution_lock')" variant="primary" @click="lockDistribution" class="mr-2">
                      <font-awesome-icon icon="lock"></font-awesome-icon>
                    </b-button>
                  </div>
                </click-confirm>
                <b-button v-if="editing && project.data.distribution_locked" variant="primary" class="mr-2" :disabled="true">
                  <font-awesome-icon icon="unlock"></font-awesome-icon>
                </b-button>
                <b-button v-b-tooltip.hover :title="$t('messages.distribution_tree')" variant="primary" @click="distribution.show = !distribution.show">
                  <font-awesome-icon icon="project-diagram"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div class="card__info bg-danger font-weight-bold text-white" v-if="(editing && project.data.delivered && !project.data.distribution_locked) || (!editing && isEqualProducts(distribution.data.products, getAllProductsFromUser(distribution.data)))">
              <p v-if="editing && project.data.delivered && !project.data.distribution_locked">{{ $t('messages.distribution_locked_update') }}</p>
              <p v-if="!editing && isEqualProducts(distribution.data.products, getAllProductsFromUser(distribution.data))">{{ $t('messages.distribution_do_nothing') }}</p>
            </div>
            <div class="card__info font-weight-bold text-inherit" v-if="!project.data.organisator && !editing">
              <p v-if="!items.pickup || (items.pickup && items.pickup.type === null)">{{ $t('messages.pickup_text_unknown') }}</p>
              <p v-if="items.pickup && items.pickup.type === 'send'">{{ $t('messages.pickup_text_send') }}</p>
              <p v-if="items.pickup && items.pickup.type === 'pickup'">
                {{ $t('messages.pickup_text_pickup_point') }} {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}{{ pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '' }} {{ $t('messages.pickup_text_pickup_point_at') }} <b-link :to="{ path: '/user/' + slugText(pickupPoint.user_id + '-' + pickupPoint.full_name) }">{{ pickupPoint.full_name }}</b-link>{{ $t('messages.pickup_text_pickup_post') }}
              </p>
              <p v-if="items.pickup && items.pickup.type === 'user'">
                {{ $t('messages.pickup_text_pickup_by') }} <b-link :to="{ path: '/user/' + slugText(pickupBy.id + '-' + pickupBy.full_name) }">{{ pickupBy.full_name }}</b-link>{{ $t('messages.pickup_text_pickup_post') }}
              </p>
            </div>
            <div ref="distributionBox" class="card__scroll overflow-x">
              <tree-view :data="distribution.data" :level="0" :admin="editing" :locked="project.data.distribution_locked"
                         :pickup-points="participants.pickupPoints" :pickup-by="participants.pickupBy"
                         @deliver="deliver" @pickup="pickup" :key="'d' + distribution.update" />
            </div>
          </div>
          <div class="card" v-if="editing && project.data && distribution.data">
            <h1 class="card__title__border d-flex">
              <div class="full-width">{{ $t('messages.shipping') }}</div>
              <div class="d-flex">
                <click-confirm v-if="editing && project.data.allow_shipping" button-size="sm" :messages="confirmMessage">
                  <div>
                    <b-button v-b-tooltip.hover :title="$t('messages.shipping_disallow')" variant="primary" @click="disallowShipping" class="mr-2">
                      <font-awesome-icon icon="times"></font-awesome-icon>
                    </b-button>
                  </div>
                </click-confirm>
                <click-confirm v-if="editing && !project.data.allow_shipping" button-size="sm" :messages="confirmMessage">
                  <div>
                    <b-button v-b-tooltip.hover :title="$t('messages.shipping_allow')" variant="primary" @click="allowShipping" class="mr-2">
                      <font-awesome-icon icon="check"></font-awesome-icon>
                    </b-button>
                  </div>
                </click-confirm>
                <click-confirm v-if="editing && !project.data.distribution_locked" button-size="sm" :messages="confirmMessage">
                  <div>
                    <b-button v-b-tooltip.hover :title="$t('messages.distribution_lock')" variant="primary" @click="lockDistribution" class="mr-2">
                      <font-awesome-icon icon="lock"></font-awesome-icon>
                    </b-button>
                  </div>
                </click-confirm>
                <b-button v-if="editing && project.data.distribution_locked" variant="primary" class="mr-2" :disabled="true"><font-awesome-icon icon="unlock"></font-awesome-icon></b-button>
                <b-button v-b-tooltip.hover :title="$t('messages.distribution_tree')" variant="primary" @click="distribution.showShipping = !distribution.showShipping">
                  <font-awesome-icon icon="project-diagram"></font-awesome-icon>
                </b-button>
              </div>
            </h1>
            <div class="card__info bg-danger font-weight-bold text-white" v-if="editing && project.data.delivered && !project.data.distribution_locked">
              <p>{{ $t('messages.distribution_locked_update') }}</p>
            </div>
            <div ref="shippingBox" class="card__scroll overflow-x">
              <tree-view v-if="distribution.data.shipments && distribution.data.shipments.length > 0"
                         :data="distribution.data" :level="0" :shipping="true" :admin="true" :locked="project.data.distribution_locked" :key="'s' + distribution.update"
                         :pickup-points="participants.pickupPoints" :pickup-by="participants.pickupBy"
                         @deliver="deliver" @ship="ship" @pickup="pickup" />
              <span v-if="!distribution.data.shipments || (distribution.data.shipments && distribution.data.shipments.length === 0)"
                    class="font-italic p-20 d-flex">{{ $t('messages.shipping_empty') }}</span>
            </div>
          </div>
          <div v-if="project.data" class="d-none" :class="{ showDistribution: distribution.show }">
            <button class="showDistributionClose" @click="distribution.show = !distribution.show">
              <font-awesome-icon icon="times-circle" size="2x" class="red" />
            </button>
            <tree-view :data="distribution.data" :chart="true" :admin="editing" :locked="project.data.distribution_locked" />
          </div>
          <div v-if="project.data" class="d-none" :class="{ showDistribution: distribution.showShipping }">
            <button class="showDistributionClose" @click="distribution.showShipping = !distribution.showShipping">
              <font-awesome-icon icon="times-circle" size="2x" class="red" />
            </button>
            <tree-view :data="distribution.data" :shipping="true" :chart="true" :admin="true" :locked="project.data.distribution_locked" />
          </div>
          <div class="card" v-if="project.data && (editing || items.taken || project.data.organisator)">
            <h1 class="card__title__border">{{ $t('messages.comments') }}</h1>
            <div class="card__scroll" v-if="comments.data && comments.data.length > 0">
              <div v-for="comment in comments.data" :key="comment.id" class="card__comment__item">
                <div>
                  <div><b-button v-if="comment.store" v-b-tooltip.hover :title="$t('messages.store_account_info')" class="mr-1 noPadding noBackground secondary"><font-awesome-icon icon="store-alt"></font-awesome-icon></b-button> <b-link :to="{ path: '/user/' + slugText(comment.user_id + '-' + comment.user_full_name) }">{{ comment.user_full_name }}</b-link></div>
                  <div class="font-italic">{{ translateDate(comment.posted_date) }}</div>
                  <div class="pt-3" v-html="comment.text"></div>
                </div>
              </div>
            </div>
            <div v-if="!comments.data || comments.data.length === 0" class="card__comment__item font-italic">
              {{ $t('messages.comments_empty') }}
            </div>
            <div class="card__bottom" v-if="!editing">
              <b-form @submit.prevent="postComment">
                <div class="card__bottom__flex">
                  <b-form-input type="text" v-model="comments.comment" required></b-form-input>
                  <notification-button :message="$t('messages.comments_post')" :notification="notifications.comment" field="comment"
                                       :disabled="!comments.comment" class="ml-2"></notification-button>
                </div>
              </b-form>
            </div>
          </div>
        </div>
      </masonry>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueMasonry from 'vue-masonry-css'
import _ from 'lodash'
import VueMoment from 'vue-moment'
import NotificationButton from '../../components/NotificationButton'
import Qrcode from '@chenfengyuan/vue-qrcode/src'
import TreeView from '../../components/project/TreeView'
import LocationPicker from '../../components/LocationPicker'
import Approval from '../../components/project/Approval'
import ProjectInfo from '../../components/project/ProjectInfo'
import Update from '../../components/project/Update'

import { defaultMixin } from '../../mixin/default'
import { projectItemMixin } from '../../mixin/project-item'
import { notificationMixin } from '../../mixin/notification'
import { productMixin } from '../../mixin/product'

import { projectService } from '../../services/project.service'
import { validationMixin } from 'vuelidate'

import { required, email, minValue, maxValue, decimal } from 'vuelidate/lib/validators'

Vue.use(VueMasonry)
Vue.use(VueMoment)

const decimalTwoRounds = (value) => {
  return value !== null && /^[-]?\d*(\.\d{0,2})?$/.test(value)
}

export default {
  name: 'project',
  metaInfo: function () {
    return {
      title: this.projectName + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.projectName,
          'vmid': 'og:title'
        },
        {
          'property': 'og:description',
          'content': this.projectDescription,
          'vmid': 'og:description'
        },
        {
          'property': 'og:image',
          'content': this.projectImage,
          'vmid': 'og:image'
        },
        {
          'property': 'description',
          'content': this.projectName,
          'vmid': 'description'
        },
        {
          'property': 'keywords',
          'content': this.projectName,
          'vmid': 'keywords'
        }
      ]
    }
  },
  mixins: [ defaultMixin, projectItemMixin, notificationMixin, validationMixin, productMixin ],
  components: { Approval, ProjectInfo, Update, LocationPicker, TreeView, Qrcode, NotificationButton },
  data: function () {
    return {
      editing: false,
      project: {
        data: null,
        deadline: false
      },
      paymentInfos: {
        data: [],
        types: [],
        id: null,
        type: null,
        name: null,
        description: null,
        extraInfo: null
      },
      items: {
        data: [],
        old: [],
        selected: [],
        selectedTemp: [],
        pickup: null,
        taken: false,
        update: 0,
        id: null,
        name: null,
        normalPrice: null,
        newPrice: null,
        normalShippingKnown: false,
        normalShipping: null,
        newShippingKnown: false,
        newShipping: null,
        minParticipants: null,
        maxParticipants: null,
        currentParticipants: 0,
        notify: true
      },
      addons: {
        data: [],
        old: [],
        selected: [],
        selectedTemp: [],
        taken: false,
        id: null,
        name: null,
        price: null,
        shippingKnown: false,
        shipping: null,
        currentParticipants: 0,
        notify: true
      },
      participants: {
        data: [],
        pickupBy: [],
        pickupPoints: [],
        selected: [],
        payment: {
          scroll: null,
          scrollUpdate: false,
          payment: null,
          paymentItems: [],
          paymentAddons: [],
          paymentAdvanced: false,
          paymentSum: null,
          paymentDescription: null,
          paymentUpdate: false,
          refundType: null,
          refund: false,
          notify: true
        },
        pickup: {
          pickupPointId: null,
          pickupPointCity: null,
          pickupPointCountry: null,
          pickupPointLat: null,
          pickupPointLng: null,
          pickupPointParticipant: null,
          pickupPointExtraInfo: null,
          pickupPointDeleting: false
        },
        update: 0
      },
      distribution: {
        data: [],
        loose: [],
        show: false,
        showShipping: false,
        update: 0
      },
      comments: {
        data: [],
        comment: null,
        interval: null
      },
      map: {
        timeout: null,
        initialized: false
      },
      notifications: {
        paymentInfo: null,
        registration: null,
        addon: null,
        payment: null,
        pickupPoint: null,
        comment: null
      }
    }
  },
  validations: function () {
    return {
      paymentInfos: {
        type: {
          required
        },
        name: {
          required
        },
        description: this.paymentInfoDescriptionValidator,
        extraInfo: this.paymentInfoExtraInfoValidator
      },
      items: {
        name: {
          required
        },
        normalPrice: {
          required,
          decimal,
          decimalTwoRounds,
          minValue: minValue(0)
        },
        newPrice: {
          required,
          decimal,
          decimalTwoRounds,
          minValue: minValue(0),
          maxValue: maxValue(this.items.normalPrice)
        },
        normalShippingKnown: {
          required
        },
        normalShipping: this.normalShippingValidator,
        newShippingKnown: {
          required
        },
        newShipping: this.newShippingValidator,
        minParticipants: {
          required,
          minValue: minValue(1),
          maxValue: maxValue(this.items.maxParticipants)
        },
        maxParticipants: {
          required,
          minValue: minValue(this.items.currentParticipants)
        }
      },
      addons: {
        name: {
          required
        },
        price: {
          required,
          decimal,
          decimalTwoRounds,
          minValue: minValue(0)
        },
        shippingKnown: {
          required
        },
        shipping: this.shippingValidator
      },
      participants: {
        pickup: {
          pickupPointCity: {
            required
          },
          pickupPointCountry: {
            required
          },
          pickupPointLat: {
            required
          },
          pickupPointLng: {
            required
          },
          pickupPointParticipant: {
            required
          }
        },
        payment: {
          paymentItems: {
            $each: {
              sum: {
                required,
                decimal,
                decimalTwoRounds,
                minValue: minValue(0)
              }
            }
          },
          paymentAddons: {
            $each: {
              sum: {
                required,
                decimal,
                decimalTwoRounds,
                minValue: minValue(0)
              }
            }
          }
        }
      },
      paymentSum: {
        required,
        decimal,
        decimalTwoRounds,
        minValue: minValue(0)
      }
    }
  },
  computed: {
    projectName: function () {
      if (this.project.data !== null && this.project.data.name !== null) {
        return this.project.data.name
      }
      return ''
    },
    projectDescription: function () {
      if (this.project.data !== null && this.project.data.description !== null) {
        return this.project.data.description.replace(/(<([^>]+)>)/ig, '')
      }
      return ''
    },
    projectImage: function () {
      if (this.project.data !== null && this.project.data.image !== null) {
        return this.project.data.image
      }
      return ''
    },
    participantIcon: function () {
      return Vue.prototype.$hostname.concat('/image/arrow-orange.png')
    },
    participantMessage: function () {
      return this.$i18n.t('messages.participant')
    },
    pickupPointIcon: function () {
      return Vue.prototype.$hostname.concat('/image/arrow-green.png')
    },
    pickupPointMessage: function () {
      return this.$i18n.t('messages.pickup')
    },
    itemsParticipant: function () {
      let self = this
      if (!this.participants.data) {
        return false
      }
      return this.participants.data.filter(function (participant) {
        if (!participant.items) {
          return false
        }
        return participant.items.filter(function (item) {
          return item.id === self.items.id
        }).length > 0
      }).length > 0
    },
    addonsParticipant: function () {
      let self = this
      if (!this.participants.data) {
        return false
      }
      return this.participants.data.filter(function (participant) {
        if (!participant.addons) {
          return false
        }
        return participant.addons.filter(function (addon) {
          return addon.id === self.addons.id
        }).length > 0
      }).length > 0
    },
    signupMessage: function () {
      if (this.items.taken) {
        if (this.project.deadline) {
          return this.$i18n.t('messages.signing_up_pickup_edit')
        } else {
          return this.$i18n.t('messages.signing_up_edit')
        }
      } else {
        return this.$i18n.t('messages.signing_up')
      }
    },
    signupButtonDisabled: function () {
      let disabled = !this.loggedIn || (!this.items.taken && this.project.deadline) || (!this.items.taken && this.items.selected.length === 0) || this.items.selected.filter(function (item) {
        return item.amount === null || item.amount === ''
      }).length > 0 || (!this.items.pickup && !this.project.data.organisator) || (this.items.taken && this.addons.taken && this.items.selected.length === 0) || this.project.data.status !== 'OPEN'
      if (!disabled && !this.items.taken) {
        let allIsFull = true
        const self = this
        this.items.data.forEach(function (item) {
          if (allIsFull && !self.isItemFull(item)) {
            allIsFull = false
          }
        })
        disabled = allIsFull
      }
      return disabled
    },
    addonMessage: function () {
      if (this.addons.taken) {
        return this.$i18n.t('messages.addons_edit')
      } else {
        return this.$i18n.t('messages.addons_add')
      }
    },
    addonButtonDisabled: function () {
      return !this.loggedIn || this.project.data.addons_locked || (!this.addons.taken && this.addons.selected.length === 0) || this.addons.selected.filter(function (addon) {
        return addon.amount === null || addon.amount === ''
      }).length > 0 || (!this.items.taken && this.items.selected.length === 0) || this.project.data.status !== 'OPEN'
    },
    registrationsFullyPaid: function () {
      let self = this
      let paidFully = this.items.taken
      this.items.old.forEach(function (item) {
        if (paidFully && item.selected && !self.isPaidFully(item)) {
          paidFully = false
        }
      })
      return paidFully
    },
    registrationsPartlyPaid: function () {
      let self = this
      let paidPartly = false
      this.items.old.forEach(function (item) {
        if (!paidPartly && item.selected && !self.isPaidFully(item)) {
          paidPartly = true
        }
      })
      return paidPartly
    },
    registrationsSumToBePaid: function () {
      let totalToPay = 0
      let totalPaid = 0
      this.items.old.forEach(function (item) {
        if (item.selected) {
          totalToPay += (item.amount * item.new_total_price)
          totalPaid += item.paid
        }
      })
      return totalToPay - totalPaid
    },
    addonsFullyPaid: function () {
      let self = this
      let paidFully = this.addons.taken
      this.addons.old.forEach(function (addon) {
        if (paidFully && addon.selected && !self.isAddonPaidFully(addon)) {
          paidFully = false
        }
      })
      return paidFully
    },
    addonsPartlyPaid: function () {
      let self = this
      let paidPartly = false
      this.addons.old.forEach(function (addon) {
        if (!paidPartly && addon.selected && !self.isAddonPaidFully(addon)) {
          paidPartly = true
        }
      })
      return paidPartly
    },
    addonsSumToBePaid: function () {
      let totalToPay = 0
      let totalPaid = 0
      this.addons.old.forEach(function (addon) {
        if (addon.selected) {
          totalToPay += (addon.amount * addon.total_price)
          totalPaid += addon.paid
        }
      })
      return totalToPay - totalPaid
    },
    totalSumToBePaid: function () {
      return Vue.options.filters.round(this.registrationsSumToBePaid + this.addonsSumToBePaid, 2)
    },
    paymentInfoDescriptionValidator: function () {
      if (this.paymentInfos.type !== null && this.paymentInfos.type.name === 'PAYPAL') {
        return { required, email }
      } else {
        return { required }
      }
    },
    paymentInfoExtraInfoValidator: function () {
      if (this.paymentInfos.type !== null && this.paymentInfos.type.name === 'IBAN') {
        return { required }
      } else {
        return { }
      }
    },
    normalShippingValidator: function () {
      if (this.items.normalShippingKnown !== null && this.items.normalShippingKnown === true) {
        return { required, minValue: minValue(0), decimal, decimalTwoRounds }
      } else {
        return { }
      }
    },
    newShippingValidator: function () {
      if (this.items.newShippingKnown !== null && this.items.newShippingKnown === true) {
        return { required, minValue: minValue(0), decimal, decimalTwoRounds }
      } else {
        return { }
      }
    },
    shippingValidator: function () {
      if (this.addons.shippingKnown !== null && this.addons.shippingKnown === true) {
        return { required, minValue: minValue(0), decimal, decimalTwoRounds }
      } else {
        return { }
      }
    },
    newPriceError: function () {
      if (!this.$v.items.newPrice.decimalTwoRounds) {
        return this.$i18n.t('errors.new_price_valid')
      } else if (!this.$v.items.newPrice.maxValue) {
        return this.$i18n.t('errors.new_price_max')
      } else {
        return this.$i18n.t('errors.new_price')
      }
    },
    minParticipantsError: function () {
      if (!this.$v.items.minParticipants.minValue) {
        return this.$i18n.t('errors.min_participants_min')
      } else if (!this.$v.items.minParticipants.maxValue) {
        return this.$i18n.t('errors.min_participants_max')
      } else {
        return this.$i18n.t('errors.min_participants')
      }
    },
    maxParticipantsError: function () {
      if (!this.$v.items.maxParticipants.minValue) {
        return this.$i18n.t('errors.max_participants_min')
      } else {
        return this.$i18n.t('errors.max_participants')
      }
    },
    totalParticipantsAmount: function () {
      let self = this
      let amount = 0
      this.participants.data.forEach(function (participant) {
        amount += self.getParticipantAmount(participant.items, participant.addons)
      })
      return amount
    },
    totalParticipantsPaid: function () {
      let self = this
      let paid = 0
      this.participants.data.forEach(function (participant) {
        paid += self.getParticipantPaid(participant.items, participant.addons)
      })
      return paid
    },
    totalParticipantsDifference: function () {
      let self = this
      let difference = 0
      this.participants.data.forEach(function (participant) {
        difference += self.getParticipantDifference(participant.items, participant.addons)
      })
      return difference
    },
    participantsBackground: function () {
      let self = this
      let fully = 0
      let partly = 0
      let nothing = 0
      this.participants.data.forEach(function (participant) {
        let check = self.getParticipantBackground(participant.items, participant.addons)
        if (check === 'fully') {
          fully += 1
        } else if (check === 'partly') {
          partly += 1
        } else {
          nothing += 1
        }
      })
      let type = ''
      if (nothing !== 0 && fully === 0 && partly === 0) {
        type = ''
      } else if (fully !== 0 && partly === 0 && nothing === 0) {
        type = 'fully'
      } else {
        type = 'partly'
      }
      let scrollable = this.participants.data.length > 5
      let classes = this.getBackgroundColor(type)
      if (scrollable) {
        classes = classes + ' card__scroll card__scroll__hide'
      }
      return classes
    },
    paymentSum: {
      get: function () {
        return this.participants.payment.paymentSum
      },
      set: function (value) {
        this.participants.payment.paymentSum = value
        let sum = value
        if (sum && sum >= 0 && this.precision(sum) <= 2) {
          // first reset all sums
          this.participants.payment.paymentItems.forEach(function (paymentItem) {
            paymentItem.sum = 0
          })
          this.participants.payment.paymentAddons.forEach(function (paymentAddon) {
            paymentAddon.sum = 0
          })
          if (this.participants.payment.refund) {
            this.participants.payment.paymentAddons.reverse().forEach(function (paymentAddon) {
              let total = parseFloat(parseFloat(paymentAddon.paid).toFixed(2))
              if (total > 0 && sum > 0) {
                let newSum = total
                if (sum < total) {
                  newSum = parseFloat(sum.toFixed(2))
                }
                paymentAddon.sum += newSum
                paymentAddon.sum = parseFloat(parseFloat(paymentAddon.sum).toFixed(2))
                paymentAddon.selected = true
                sum -= newSum
              }
            })
            this.participants.payment.paymentItems.reverse().forEach(function (paymentItem) {
              let total = parseFloat(parseFloat(paymentItem.paid).toFixed(2))
              if (total > 0 && sum > 0) {
                let newSum = total
                if (sum < total) {
                  newSum = parseFloat(sum.toFixed(2))
                }
                paymentItem.sum += newSum
                paymentItem.sum = parseFloat(parseFloat(paymentItem.sum).toFixed(2))
                paymentItem.selected = true
                sum -= newSum
              }
            })
          } else {
            this.participants.payment.paymentItems.forEach(function (paymentItem) {
              let total = parseFloat(((paymentItem.new_total_price * paymentItem.amount) - paymentItem.paid).toFixed(2))
              if (total > 0 && sum > 0) {
                let newSum = total
                if (sum < total) {
                  newSum = parseFloat(sum.toFixed(2))
                }
                paymentItem.sum += newSum
                paymentItem.sum = parseFloat(parseFloat(paymentItem.sum).toFixed(2))
                paymentItem.selected = true
                sum -= newSum
              }
            })
            this.participants.payment.paymentAddons.forEach(function (paymentAddon) {
              let total = parseFloat(((paymentAddon.total_price * paymentAddon.amount) - paymentAddon.paid).toFixed(2))
              if (total > 0 && sum > 0) {
                let newSum = total
                if (sum < total) {
                  newSum = parseFloat(sum.toFixed(2))
                }
                paymentAddon.sum += newSum
                paymentAddon.sum = parseFloat(parseFloat(paymentAddon.sum).toFixed(2))
                paymentAddon.selected = true
                sum -= newSum
              }
            })
          }
          if (sum > 0) {
            // if still left, then book the rest on the first item
            this.participants.payment.paymentItems[0].sum += sum
            this.participants.payment.paymentItems[0].sum = parseFloat(this.participants.payment.paymentItems[0].sum.toFixed(2))
          }
        }
      }
    },
    pickupPoint: function () {
      let self = this
      return this.participants.pickupPoints.filter(function (pickupPoint) {
        return pickupPoint.id === self.items.pickup.id
      })[0]
    },
    pickupBy: function () {
      let self = this
      return this.participants.pickupBy.filter(function (pickupBy) {
        return pickupBy.id === self.items.pickup.id
      })[0]
    },
    pickupPointParticipants: function () {
      let self = this
      let participantsList = []
      if (this.participants.data) {
        participantsList = this.participants.data.slice()
      }
      let organisator = participantsList.filter(function (participant) {
        return self.project.data.user_id === participant.id
      })
      if (organisator.length === 0) {
        participantsList.push({ 'id': this.project.data.user_id, 'full_name': this.project.data.user_full_name })
        participantsList.sort(function (a, b) {
          let x = a.full_name.toLowerCase()
          let y = b.full_name.toLowerCase()
          if (x < y) {
            return -1
          }
          if (x > y) {
            return 1
          }
          return 0
        })
      }
      return participantsList
    }
  },
  methods: {
    setDeadline: function (deadline) {
      this.project.deadline = deadline
    },
    handleItemChange: function (index, item) {
      let selectedItemArray = this.items.selected.filter(function (selectedItem) {
        return selectedItem.id === item.id
      })
      let selectedItem = null
      if (selectedItemArray.length > 0) {
        selectedItem = selectedItemArray[0]
      }
      if (selectedItem != null) {
        item.amount = 0
      } else {
        item.amount = 1
      }
    },
    handleItemInputChange: function (item) {
      let oldAmount = this.getOldItemAmount(item)
      if (item.amount > (oldAmount + item.max_participants - item.current_participants)) {
        item.amount = oldAmount + item.max_participants - item.current_participants
        this.items.update += 1
      }
    },
    handleItemBlurChange: function (item) {
      if (item.amount < Math.ceil(item.paid / item.new_total_price)) {
        item.amount = Math.ceil(item.paid / item.new_total_price)
        this.items.update += 1
      }
    },
    handleAddonChange: function (index, addon) {
      let selectedAddonArray = this.addons.selected.filter(function (selectedAddon) {
        return selectedAddon.id === addon.id
      })
      let selectedAddon = null
      if (selectedAddonArray.length > 0) {
        selectedAddon = selectedAddonArray[0]
      }
      if (selectedAddon != null) {
        addon.amount = 0
      } else {
        addon.amount = 1
      }
    },
    handlePaymentInfoSelectChange: function () {
      if (this.paymentInfos.description) {
        this.$v.paymentInfos.description.$touch()
      }
      this.$v.paymentInfos.type.$touch()
    },
    getOldItemAmount: function (item) {
      let oldAmount = 0
      let oldItem = this.getOldItem(item)
      if (oldItem !== null) {
        oldAmount = oldItem.amount
      }
      return oldAmount
    },
    getOldItem: function (item) {
      let oldItem = null
      let oldItemList = this.items.old.filter(function (oldItem) {
        return oldItem.id === item.id
      })
      if (oldItemList.length > 0) {
        oldItem = oldItemList[0]
      }
      return oldItem
    },
    getOldAddonAmount: function (addon) {
      let oldAmount = 0
      let oldAddon = this.getOldAddon(addon)
      if (oldAddon !== null) {
        oldAmount = oldAddon.amount
      }
      return oldAmount
    },
    getOldAddon: function (addon) {
      let oldAddon = null
      let oldAddonList = this.addons.old.filter(function (oldAddon) {
        return oldAddon.id === addon.id
      })
      if (oldAddonList.length > 0) {
        oldAddon = oldAddonList[0]
      }
      return oldAddon
    },
    cardItemBackground: function (item) {
      if (this.isItemStillJoinable(item)) {
        return 'bg-success'
      } else if (!this.isItemSet(item)) {
        return 'bg-warning'
      } else {
        return 'bg-danger'
      }
    },
    fetchProject: function () {
      let id = this.$route.params.id
      let store = this.$store

      projectService.get(id)
        .then((project) => {
          if (project.status === 'CANCELLED') {
            store.commit('warning', 'errors.project_cancelled')
          } else if (project.private && !this.loggedIn) {
            store.commit('error', 'errors.project')
          } else {
            this.project.data = project
          }
        }, () => {
          store.commit('error', 'errors.project')
        })

      this.fetchPaymentInfos(id)
      this.fetchItems(id)
      this.fetchAddons(id)
      this.fetchPickupPoints(id)
      this.fetchDistribution(id)
      this.fetchDistributionLoose(id)
      this.fetchParticipants(id)
      this.fetchComments(id)
    },
    isPaidFully: function (item) {
      let sum = 0
      if (item.sum) {
        sum = parseFloat(item.sum)
        if (this.participants.payment.refund) {
          sum *= -1
        }
      }
      let totalSum = parseFloat(parseFloat(item.paid + sum).toFixed(2))
      return (totalSum > 0 && item.amount > 0 && totalSum >= parseFloat(parseFloat(item.amount * item.new_total_price).toFixed(2))) || item.new_total_price === 0
    },
    isPaidPartly: function (item) {
      let sum = 0
      if (item.sum) {
        sum = parseFloat(item.sum)
        if (this.participants.payment.refund) {
          sum *= -1
        }
      }
      let totalSum = parseFloat(parseFloat(item.paid + sum).toFixed(2))
      return totalSum > 0 && item.amount > 0 && !this.isPaidFully(item)
    },
    isAddonPaidFully: function (addon) {
      let sum = 0
      if (addon.sum) {
        sum = parseFloat(addon.sum)
        if (this.participants.payment.refund) {
          sum *= -1
        }
      }
      let totalSum = parseFloat(parseFloat(addon.paid + sum).toFixed(2))
      return (totalSum > 0 && addon.amount > 0 && totalSum >= parseFloat(parseFloat(addon.amount * addon.total_price).toFixed(2))) || addon.total_price === 0
    },
    isAddonPaidPartly: function (addon) {
      let sum = 0
      if (addon.sum) {
        sum = parseFloat(addon.sum)
        if (this.participants.payment.refund) {
          sum *= -1
        }
      }
      let totalSum = parseFloat(parseFloat(addon.paid + sum).toFixed(2))
      return totalSum > 0 && addon.amount > 0 && !this.isAddonPaidFully(addon)
    },
    getParticipantAmount: function (items, addons) {
      let amount = 0
      if (items && items.length > 0) {
        items.forEach(function (item) {
          amount += (item.new_total_price * item.amount)
        })
      }
      if (addons && addons.length > 0) {
        addons.forEach(function (addon) {
          amount += (addon.total_price * addon.amount)
        })
      }
      return amount
    },
    getParticipantPaid: function (items, addons) {
      let self = this
      let paid = 0
      if (items && items.length > 0) {
        items.forEach(function (item) {
          let sum = 0
          let itemSumList = self.participants.payment.paymentItems.filter(function (itemSum) {
            return itemSum.id === item.id
          })
          if (itemSumList.length > 0 && itemSumList[0].sum) {
            sum = parseFloat(itemSumList[0].sum)
            if (self.participants.payment.refund) {
              sum *= -1
            }
          }
          paid += parseFloat(parseFloat(item.paid + sum).toFixed(2))
        })
      }
      if (addons && addons.length > 0) {
        addons.forEach(function (addon) {
          let sum = 0
          let addonSumList = self.participants.payment.paymentAddons.filter(function (addonSum) {
            return addonSum.id === addon.id
          })
          if (addonSumList.length > 0 && addonSumList[0].sum) {
            sum = parseFloat(addonSumList[0].sum)
            if (self.participants.payment.refund) {
              sum *= -1
            }
          }
          paid += parseFloat(parseFloat(addon.paid + sum).toFixed(2))
        })
      }
      return paid
    },
    getParticipantDifference: function (items, addons) {
      let self = this
      let difference = 0
      if (items && items.length > 0) {
        items.forEach(function (item) {
          let sum = 0
          let itemSumList = self.participants.payment.paymentItems.filter(function (itemSum) {
            return itemSum.id === item.id
          })
          if (itemSumList.length > 0 && itemSumList[0].sum) {
            sum = parseFloat(itemSumList[0].sum)
            if (self.participants.payment.refund) {
              sum *= -1
            }
          }
          difference += (parseFloat(parseFloat(item.paid + sum).toFixed(2)) - (parseFloat(parseFloat(item.new_total_price * item.amount).toFixed(2))))
        })
      }
      if (addons && addons.length > 0) {
        addons.forEach(function (addon) {
          let sum = 0
          let addonSumList = self.participants.payment.paymentAddons.filter(function (addonSum) {
            return addonSum.id === addon.id
          })
          if (addonSumList.length > 0 && addonSumList[0].sum) {
            sum = parseFloat(addonSumList[0].sum)
            if (self.participants.payment.refund) {
              sum *= -1
            }
          }
          difference += (parseFloat(parseFloat(addon.paid + sum).toFixed(2)) - (parseFloat(parseFloat(addon.total_price * addon.amount).toFixed(2))))
        })
      }
      return difference
    },
    getParticipantBackground: function (items, addons) {
      let self = this
      let itemsPaidFully = !items || items.length === 0
      let itemsPaidPartly = false
      let addonsPaidFully = !addons || addons.length === 0
      let addonsPaidPartly = false

      if (items && items.length > 0) {
        itemsPaidFully = true
        items.forEach(function (item) {
          if (itemsPaidFully && !self.isPaidFully(item)) {
            itemsPaidFully = false
          }
        })
        if (!itemsPaidFully) {
          itemsPaidPartly = false
          items.forEach(function (item) {
            if (!itemsPaidPartly && self.isPaidPartly(item)) {
              itemsPaidPartly = true
            }
          })
        }
      }
      if (addons && addons.length > 0) {
        addonsPaidFully = true
        addons.forEach(function (addon) {
          if (addonsPaidFully && !self.isAddonPaidFully(addon)) {
            addonsPaidFully = false
          }
        })
        if (!addonsPaidFully) {
          addonsPaidPartly = false
          addons.forEach(function (addon) {
            if (!addonsPaidPartly && self.isAddonPaidPartly(addon)) {
              addonsPaidPartly = true
            }
          })
        }
      }

      if ((!itemsPaidFully && !addonsPaidFully && !itemsPaidPartly && !addonsPaidPartly) ||
        ((!items || items.length === 0) && !addonsPaidFully && !addonsPaidPartly) ||
        ((!addons || addons.length === 0) && !itemsPaidFully && !itemsPaidPartly)) {
        return ''
      } else if (itemsPaidFully && addonsPaidFully) {
        return 'fully'
      } else {
        return 'partly'
      }
    },
    getItemsBackground: function (items) {
      if (items && items.length > 0) {
        let self = this
        let paidFully = true
        items.forEach(function (item) {
          if (paidFully && !self.isPaidFully(item)) {
            paidFully = false
          }
        })
        if (paidFully) {
          return 'fully'
        } else {
          let paidPartly = false
          items.forEach(function (item) {
            if (!paidPartly && self.isPaidPartly(item)) {
              paidPartly = true
            }
          })
          if (paidPartly) {
            return 'partly'
          } else {
            return ''
          }
        }
      } else {
        return ''
      }
    },
    getItemBackground: function (item) {
      if (this.isPaidFully(item)) {
        return 'fully'
      } else if (this.isPaidPartly(item)) {
        return 'partly'
      } else {
        return ''
      }
    },
    getAddonsBackground: function (addons) {
      if (addons && addons.length > 0) {
        let self = this
        let paidFully = true
        addons.forEach(function (addon) {
          if (paidFully && !self.isAddonPaidFully(addon)) {
            paidFully = false
          }
        })
        if (paidFully) {
          return 'fully'
        } else {
          let paidPartly = false
          addons.forEach(function (addon) {
            if (!paidPartly && self.isAddonPaidPartly(addon)) {
              paidPartly = true
            }
          })
          if (paidPartly) {
            return 'partly'
          } else {
            return ''
          }
        }
      } else {
        return ''
      }
    },
    getAddonBackground: function (addon) {
      if (this.isAddonPaidFully(addon)) {
        return 'fully'
      } else if (this.isAddonPaidPartly(addon)) {
        return 'partly'
      } else {
        return ''
      }
    },
    getBackgroundColor: function (status) {
      if (status === 'partly') {
        return 'bg-warning text-white font-weight-bold'
      } else if (status === 'fully') {
        return 'bg-success text-white font-weight-bold'
      } else {
        return 'font-weight-bold'
      }
    },
    signup: function () {
      let store = this.$store

      // don't push amount 0
      let saveItems = this.items.selected.filter(function (selectedItem) {
        return selectedItem.amount > 0
      })

      if (this.project.data.status === 'OPEN') {
        if (!this.project.deadline) {
          projectService.updateItems(this.project.data.id, saveItems)
            .then(() => {
              if (saveItems.length === 0) {
                this.items.pickup = null
              }
              if (!this.project.data.organisator) {
                projectService.updatePickup(this.project.data.id, this.items.pickup)
                  .then(() => {
                    if (this.items.taken) {
                      store.commit('success', 'success.signing_up_edit')
                    } else {
                      store.commit('success', 'success.signing_up')
                    }
                    this.sendNotification('registration', 'success')
                    this.fetchItems(this.project.data.id)
                  }, () => {
                    if (this.items.taken) {
                      store.commit('error', 'errors.signing_up_edit')
                    } else {
                      store.commit('error', 'errors.signing_up')
                    }
                    this.sendNotification('registration', 'error')
                  })
              } else {
                if (this.items.taken) {
                  store.commit('success', 'success.signing_up_edit')
                } else {
                  store.commit('success', 'success.signing_up')
                }
                this.sendNotification('registration', 'success')
                this.fetchItems(this.project.data.id)
              }
            }, () => {
              if (this.items.taken) {
                store.commit('error', 'errors.signing_up_edit')
              } else {
                store.commit('error', 'errors.signing_up')
              }
              this.sendNotification('registration', 'error')
            })
        } else if (!this.project.data.organisator) {
          projectService.updatePickup(this.project.data.id, this.items.pickup)
            .then(() => {
              if (this.items.taken) {
                store.commit('success', 'success.signing_up_edit')
              } else {
                store.commit('success', 'success.signing_up')
              }
              this.sendNotification('registration', 'success')
            }, () => {
              if (this.items.taken) {
                store.commit('error', 'errors.signing_up_edit')
              } else {
                store.commit('error', 'errors.signing_up')
              }
              this.sendNotification('registration', 'error')
            })
        } else {
          if (this.items.taken) {
            store.commit('success', 'success.signing_up_edit')
          } else {
            store.commit('success', 'success.signing_up')
          }
          this.sendNotification('registration', 'success')
          this.fetchItems(this.project.data.id)
        }
      }
    },
    signupDisabled: function (item) {
      return this.signupAmountDisabled(item) || item.paid > 0
    },
    signupAmountDisabled: function (item) {
      return !this.loggedIn || this.project.deadline || this.project.data.status !== 'OPEN' || (!this.items.taken && this.isItemFull(item))
    },
    saveAddons: function () {
      let store = this.$store

      // don't push amount 0
      let saveAddons = this.addons.selected.filter(function (selectedAddon) {
        return selectedAddon.amount > 0
      })

      projectService.updateAddons(this.project.data.id, saveAddons)
        .then(() => {
          if (this.addons.taken) {
            store.commit('success', 'success.addons_edit')
          } else {
            store.commit('success', 'success.addons_add')
          }
          this.sendNotification('addon', 'success')
          this.fetchAddons(this.project.data.id)
        }, error => {
          if (this.addons.taken) {
            store.commit('error', 'errors.addons_edit')
          } else if (error.response.status === 400) {
            store.commit('error', 'errors.addons_register')
          } else {
            store.commit('error', 'errors.addons_add')
          }
          this.sendNotification('addon', 'error')
        })
    },
    lockAddons: function () {
      let store = this.$store

      projectService.lockAddons(this.project.data.id)
        .then(() => {
          store.commit('success', 'success.addons_locked')
          this.project.data.addons_locked = true
        }, () => {
          store.commit('error', 'errors.addons_locked')
        })
    },
    unlockAddons: function () {
      let store = this.$store

      projectService.unlockAddons(this.project.data.id)
        .then(() => {
          store.commit('success', 'success.addons_unlocked')
          this.project.data.addons_locked = false
        }, () => {
          store.commit('error', 'errors.addons_unlocked')
        })
    },
    addonDisabled: function (addon) {
      return this.addonAmountDisabled(addon) || addon.paid > 0
    },
    addonAmountDisabled: function (addon) {
      return !this.loggedIn || this.project.data.addons_locked ||
        (!this.items.taken && this.items.selected.length === 0) || this.project.data.status !== 'OPEN'
    },
    postComment: function () {
      let store = this.$store
      projectService.postComment(this.project.data.id, this.comments.comment)
        .then(() => {
          this.comments.comment = null
          this.sendNotification('comment', 'success')
          this.fetchComments(this.project.data.id)
        }, () => {
          store.commit('error', 'errors.comment')
          this.sendNotification('comment', 'error')
        })
    },
    removeCommentPuller: function () {
      clearInterval(this.comments.interval)
    },
    fetchPaymentInfos: function (id) {
      let store = this.$store
      projectService.fetchPaymentInfos(id)
        .then((paymentInfos) => {
          this.paymentInfos.data = paymentInfos
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    fetchItems: function (id) {
      let store = this.$store
      projectService.fetchItems(id)
        .then((items) => {
          this.items.data = items
          this.items.old = JSON.parse(JSON.stringify(items))
          this.items.selected = items.filter(function (item) {
            return item.selected
          })
          this.items.selectedTemp = []
          let selectedItemsTempReference = this.items.selectedTemp
          items.forEach(function (item, index) {
            if (item.selected) {
              selectedItemsTempReference.splice(index, 1, item)
            } else {
              selectedItemsTempReference.splice(index, 1, false)
            }
          })
          this.items.taken = this.items.selected.length > 0
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    fetchAddons: function (id) {
      let store = this.$store
      projectService.fetchAddons(id)
        .then((addons) => {
          this.addons.data = addons
          this.addons.old = JSON.parse(JSON.stringify(addons))
          this.addons.selected = addons.filter(function (addon) {
            return addon.selected
          })
          this.addons.selectedTemp = []
          let selectedAddonsTempReference = this.addons.selectedTemp
          addons.forEach(function (addon, index) {
            if (addon.selected) {
              selectedAddonsTempReference.splice(index, 1, addon)
            } else {
              selectedAddonsTempReference.splice(index, 1, false)
            }
          })
          this.addons.taken = this.addons.selected.length > 0
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    fetchParticipants: function (id) {
      let store = this.$store
      projectService.fetchParticipants(id)
        .then((participants) => {
          this.participants.data = participants
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    fetchPickupPoints: function (id) {
      let store = this.$store
      projectService.fetchPickupPoints(id)
        .then((pickupPoints) => {
          this.participants.pickupPoints = pickupPoints
        }, () => {
          store.commit('error', 'errors.project')
        })
    },
    fetchDistribution: function (id) {
      let store = this.$store
      if (this.editing) {
        projectService.fetchDistributionEdit(id)
          .then((distribution) => {
            this.distribution.data = distribution
          }, () => {
            store.commit('error', 'errors.project')
          })
      } else {
        projectService.fetchDistribution(id)
          .then((distribution) => {
            this.distribution.data = distribution
          }, () => {
            store.commit('error', 'errors.project')
          })
      }
    },
    fetchDistributionLoose: function (id) {
      if (this.editing) {
        let store = this.$store
        projectService.fetchDistributionLoose(id)
          .then((distribution) => {
            this.distribution.loose = distribution
          }, () => {
            store.commit('error', 'errors.project')
          })
      }
    },
    fetchComments: function (id) {
      projectService.fetchComments(id)
        .then((comments) => {
          this.comments.data = comments
        }, () => {})
    },
    initializePickupOptions: function () {
      if (this.project.data && this.items.data && this.items.data.length > 0 && this.participants.pickupPoints && this.participants.pickupPoints.length > 0) {
        if (this.participants.data) {
          let pickupPointsList = this.participants.pickupPoints
          this.participants.pickupBy = this.participants.data.filter(function (participant) {
            let filteredPickupPoints = pickupPointsList.filter(function (pickupPoint) {
              return pickupPoint.user_id === participant.id
            })
            return filteredPickupPoints.length === 0
          })
          this.participants.pickupBy.sort(function (a, b) {
            let x = a.full_name.toLowerCase()
            let y = b.full_name.toLowerCase()
            if (x < y) {
              return -1
            }
            if (x > y) {
              return 1
            }
            return 0
          })
        }
        if (this.items.taken && !this.items.pickup) {
          if (this.project.data.pickup_type) {
            this.items.pickup = JSON.parse('{ "type": "' + this.project.data.pickup_type + '", "id": ' + this.project.data.pickup_id + ' }')
          } else {
            this.items.pickup = JSON.parse('{ "type": null, "id": null }')
          }
        }
      }
    },
    clearPaymentInfo: function () {
      this.paymentInfos.id = null
      this.paymentInfos.type = null
      this.paymentInfos.name = null
      this.paymentInfos.description = null
      this.paymentInfos.extraInfo = null
    },
    savePaymentInfo: function () {
      projectService.postPaymentInfo(this.project.data.id,
        { 'id': this.paymentInfos.id,
          'type': this.paymentInfos.type.id,
          'name': this.paymentInfos.name,
          'description': this.paymentInfos.description,
          'extra_info': this.paymentInfos.extraInfo })
        .then(() => {
          this.clearPaymentInfo()
          this.fetchPaymentInfos(this.project.data.id)
          if (this.paymentInfos.id) {
            this.$store.commit('success', 'success.payment_info_edit')
          } else {
            this.$store.commit('success', 'success.payment_info_add')
          }
        }, () => {
          if (this.paymentInfos.id) {
            this.$store.commit('error', 'errors.payment_info_edit')
          } else {
            this.$store.commit('error', 'errors.payment_info_add')
          }
          this.sendNotification('paymentInfo', 'error')
        })
    },
    deletePaymentInfo: function (paymentInfo) {
      projectService.deletePaymentInfo(paymentInfo.id)
        .then(() => {
          this.fetchPaymentInfos(this.project.data.id)
          this.$store.commit('success', 'success.payment_info_delete')
        }, () => this.$store.commit('error', 'errors.payment_info_delete'))
    },
    initializePaymentInfo: function (paymentInfo) {
      this.$v.paymentInfos.$reset()

      if (this.paymentInfos.types.length === 0) {
        projectService.fetchPaymentInfoTypes()
          .then((paymentInfoTypes) => {
            this.paymentInfos.types = paymentInfoTypes
            this.initializePaymentInfoData(paymentInfo)
          }, () => {})
      } else {
        this.initializePaymentInfoData(paymentInfo)
      }
    },
    initializePaymentInfoData: function (paymentInfo) {
      if (paymentInfo !== null) {
        this.paymentInfos.id = paymentInfo.id
        this.paymentInfos.type = this.paymentInfos.types.filter(function (paymentInfoType) {
          return paymentInfoType.name === paymentInfo.payment_type
        })[0]
        this.paymentInfos.name = paymentInfo.name
        this.paymentInfos.description = paymentInfo.description
        this.paymentInfos.extraInfo = paymentInfo.extra_info
        this.$v.paymentInfos.$touch()
      } else {
        this.clearPaymentInfo()
        this.paymentInfos.name = ''
        this.paymentInfos.description = ''
      }
    },
    getTotalOrderedRegistrations: function (item) {
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.items && participant.items.length > 0) {
            participant.items.forEach(function (participantItem) {
              if (participantItem.id === item.id) {
                total += participantItem.amount
              }
            })
          }
        })
      }
      return total
    },
    getTotalPaidOrderedRegistrations: function (item) {
      let self = this
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.items && participant.items.length > 0) {
            participant.items.forEach(function (participantItem) {
              if (participantItem.id === item.id && self.isPaidFully(participantItem)) {
                total += participantItem.amount
              }
            })
          }
        })
      }
      return total
    },
    getTotalPartlyPaidOrderedRegistrations: function (item) {
      let self = this
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.items && participant.items.length > 0) {
            participant.items.forEach(function (participantItem) {
              if (participantItem.id === item.id && self.isPaidPartly(participantItem)) {
                total += participantItem.amount
              }
            })
          }
        })
      }
      return total
    },
    getTotalOrderedAddons: function (addon) {
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.addons && participant.addons.length > 0) {
            participant.addons.forEach(function (participantAddon) {
              if (participantAddon.id === addon.id) {
                total += participantAddon.amount
              }
            })
          }
        })
      }
      return total
    },
    getTotalPaidOrderedAddons: function (addon) {
      let self = this
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.addons && participant.addons.length > 0) {
            participant.addons.forEach(function (participantAddon) {
              if (participantAddon.id === addon.id && self.isAddonPaidFully(participantAddon)) {
                total += participantAddon.amount
              }
            })
          }
        })
      }
      return total
    },
    getTotalPartlyPaidOrderedAddons: function (addon) {
      let self = this
      let total = 0
      if (this.participants.data && this.participants.data.length > 0) {
        this.participants.data.forEach(function (participant) {
          if (participant.addons && participant.addons.length > 0) {
            participant.addons.forEach(function (participantAddon) {
              if (participantAddon.id === addon.id && self.isAddonPaidPartly(participantAddon)) {
                total += participantAddon.amount
              }
            })
          }
        })
      }
      return total
    },
    clearItem: function () {
      this.items.id = null
      this.items.name = null
      this.items.normalPrice = null
      this.items.newPrice = null
      this.items.normalShipping = 0
      this.items.normalShippingKnown = false
      this.items.newShipping = 0
      this.items.newShippingKnown = false
      this.items.minParticipants = null
      this.items.maxParticipants = null
      this.items.currentParticipants = 0
    },
    saveItem: function () {
      projectService.postItem(this.project.data.id,
        { 'id': this.items.id,
          'name': this.items.name,
          'normal_price': this.items.normalPrice,
          'new_price': this.items.newPrice,
          'normal_shipping': this.items.normalShipping,
          'normal_shipping_known': this.items.normalShippingKnown,
          'new_shipping': this.items.newShipping,
          'new_shipping_known': this.items.newShippingKnown,
          'min_participants': this.items.minParticipants,
          'max_participants': this.items.maxParticipants,
          'notify': this.items.notify })
        .then(() => {
          if (this.items.id) {
            this.$store.commit('success', 'success.item_edit')
          } else {
            this.$store.commit('success', 'success.item_add')
          }
          this.clearItem()
          this.fetchItems(this.project.data.id)
        }, () => {
          if (this.items.id) {
            this.$store.commit('error', 'errors.item_edit')
          } else {
            this.$store.commit('error', 'errors.item_add')
          }
          this.sendNotification('registration', 'error')
        })
    },
    deleteItem: function (item) {
      projectService.deleteItem(item.id)
        .then(() => {
          this.fetchItems(this.project.data.id)
          this.$store.commit('success', 'success.item_delete')
        }, () => this.$store.commit('error', 'errors.item_delete'))
    },
    initializeItem: function (item) {
      this.fetchParticipants(this.project.data.id)
      this.$v.items.$reset()
      if (item !== null) {
        this.items.id = item.id
        this.items.name = item.name
        this.items.normalPrice = item.normal_price
        this.items.newPrice = item.new_price
        this.items.normalShipping = item.normal_shipping
        this.items.normalShippingKnown = item.normal_shipping_known
        this.items.newShipping = item.new_shipping
        this.items.newShippingKnown = item.new_shipping_known
        this.items.minParticipants = item.min_participants
        this.items.maxParticipants = item.max_participants
        this.items.currentParticipants = item.current_participants
      } else {
        this.clearItem()
        this.items.name = ''
      }
    },
    clearAddon: function () {
      this.addons.id = null
      this.addons.name = null
      this.addons.price = null
      this.addons.shipping = 0
      this.addons.shippingKnown = false
      this.addons.currentParticipants = 0
    },
    saveAddon: function () {
      projectService.postAddon(this.project.data.id,
        { 'id': this.addons.id,
          'name': this.addons.name,
          'price': this.addons.price,
          'shipping': this.addons.shipping,
          'shipping_known': this.addons.shippingKnown,
          'notify': this.addons.notify})
        .then(() => {
          if (this.addons.id) {
            this.$store.commit('success', 'success.addon_edit')
          } else {
            this.$store.commit('success', 'success.addon_add')
          }
          this.clearAddon()
          this.fetchAddons(this.project.data.id)
        }, () => {
          if (this.items.id) {
            this.$store.commit('error', 'errors.addon_edit')
          } else {
            this.$store.commit('error', 'errors.addon_add')
          }
          this.sendNotification('addon', 'error')
        })
    },
    deleteAddon: function (addon) {
      projectService.deleteAddon(addon.id)
        .then(() => {
          this.fetchAddons(this.project.data.id)
          this.$store.commit('success', 'success.addon_delete')
        }, () => this.$store.commit('error', 'errors.addon_delete'))
    },
    initializeAddon: function (addon) {
      this.fetchParticipants(this.project.data.id)
      this.$v.addons.$reset()
      if (addon !== null) {
        this.addons.id = addon.id
        this.addons.name = addon.name
        this.addons.price = addon.price
        this.addons.shipping = addon.shipping
        this.addons.shippingKnown = addon.shipping_known
        this.addons.currentParticipants = addon.current_participants
      } else {
        this.clearAddon()
        this.addons.name = ''
      }
    },
    selectParticipant: function (participant) {
      if (this.isSelectedParticipant(participant)) {
        for (let i = 0; i < this.participants.selected.length; i++) {
          if (this.participants.selected[i] === participant.id) {
            this.participants.selected.splice(i, 1)
          }
        }
      } else {
        this.participants.selected.push(participant.id)
      }
    },
    isSelectedParticipant: function (participant) {
      return this.participants.selected.filter(function (selected) {
        return selected === participant.id
      }).length > 0
    },
    unsubscribe: function (participant) {
      participant.loadingUnsubscribe = true
      this.participants.update += 1
      projectService.unsubscribe(this.project.data.id, participant.id)
        .then(() => {
          this.participants.payment.scroll = document.getElementById('paymentScroll').scrollTop
          this.participants.payment.scrollUpdate = true
          this.$store.commit('success', 'success.unsubscribe')
          this.participants.data = this.participants.data.filter(function (participantItem) {
            return participantItem.id !== participant.id
          })
          this.fetchDistribution(this.project.data.id)
          this.fetchDistributionLoose(this.project.data.id)
        }, () => {
          this.$store.commit('error', 'errors.unsubscribe')
          participant.loadingUnsubscribe = false
          this.participants.update += 1
        })
    },
    preparePaymentParticipant: function (participant, refund) {
      this.participants.payment.scroll = document.getElementById('paymentScroll').scrollTop
      this.participants.payment.scrollUpdate = false
      this.participants.payment.paymentUpdate = true
      let self = this
      this.$v.participants.payment.$reset()
      this.$v.paymentSum.$reset()
      this.participants.payment.paymentItems = []
      if (participant.items && participant.items.length > 0) {
        participant.items.forEach(function (item) {
          let paymentItem = {
            id: item.id,
            amount: item.amount,
            name: item.name,
            new_total_price: item.new_total_price,
            paid: item.paid,
            selected: false,
            sum: 0
          }
          self.participants.payment.paymentItems.push(paymentItem)
        })
      }
      this.participants.payment.paymentAddons = []
      if (participant.addons && participant.addons.length > 0) {
        participant.addons.forEach(function (addon) {
          let paymentAddon = {
            id: addon.id,
            amount: addon.amount,
            name: addon.name,
            total_price: addon.total_price,
            paid: addon.paid,
            selected: false,
            sum: 0
          }
          self.participants.payment.paymentAddons.push(paymentAddon)
        })
      }
      this.participants.payment.paymentSum = 0
      this.participants.payment.paymentDescription = null
      this.participants.payment.payment = participant
      this.participants.payment.refundType = null
      this.participants.payment.refund = refund
    },
    clearPaymentParticipant: function () {
      this.$v.participants.payment.$reset()
      this.$v.paymentSum.$reset()
      this.participants.payment.paymentItems = []
      this.participants.payment.paymentAddons = []
      this.participants.payment.paymentSum = null
      this.participants.payment.paymentDescription = null
      this.participants.payment.payment = null
      this.participants.payment.refundType = null
      this.participants.payment.refund = false
      this.participants.payment.scrollUpdate = true
    },
    switchPaymentAdvanced: function () {
      let self = this
      this.participants.payment.paymentUpdate = true
      this.participants.payment.paymentAdvanced = !this.participants.payment.paymentAdvanced
      this.participants.payment.paymentSum = 0
      this.participants.payment.paymentItems.forEach(function (paymentItem) {
        if (paymentItem.sum) {
          self.participants.payment.paymentSum += paymentItem.sum
        }
      })
      this.participants.payment.paymentAddons.forEach(function (paymentAddon) {
        if (paymentAddon.sum) {
          self.participants.payment.paymentSum += paymentAddon.sum
        }
      })
      this.paymentSum = parseFloat(this.paymentSum.toFixed(2))
    },
    switchItemPayment: function (item) {
      for (let i = 0; i < this.participants.payment.paymentItems.length; i++) {
        if (this.participants.payment.paymentItems[i].id === item.id) {
          this.participants.payment.paymentItems[i].selected = !this.participants.payment.paymentItems[i].selected
          if (!this.participants.payment.paymentItems[i].selected) {
            this.participants.payment.paymentItems[i].sum = null
          }
        }
      }
    },
    hasItemPayment: function (item) {
      return this.participants.payment.paymentItems.filter(function (paymentItem) {
        return paymentItem.id === item.id && paymentItem.selected
      }).length > 0
    },
    switchAddonPayment: function (addon) {
      for (let i = 0; i < this.participants.payment.paymentAddons.length; i++) {
        if (this.participants.payment.paymentAddons[i].id === addon.id) {
          this.participants.payment.paymentAddons[i].selected = !this.participants.payment.paymentAddons[i].selected
          if (!this.participants.payment.paymentAddons[i].selected) {
            this.participants.payment.paymentAddons[i].sum = null
          }
        }
      }
    },
    hasAddonPayment: function (addon) {
      return this.participants.payment.paymentAddons.filter(function (paymentAddon) {
        return paymentAddon.id === addon.id && paymentAddon.selected
      }).length > 0
    },
    savePaymentParticipant: function () {
      let self = this
      let itemPayments = []
      let items = this.participants.payment.paymentItems.filter(function (item) {
        return item.sum !== null && item.sum !== '' && parseFloat(item.sum) > 0
      })
      let transformedItems = []
      items.forEach(function (item) {
        transformedItems.push({
          id: item.id,
          amount: parseFloat(item.sum)
        })
        itemPayments.push({
          id: item.id,
          amount: parseFloat(item.sum),
          payment_date: new Date().toJSON().replace(/-/g, '/')
        })
      })
      let addonPayments = []
      let addons = this.participants.payment.paymentAddons.filter(function (addon) {
        return addon.sum !== null && addon.sum !== '' && parseFloat(addon.sum) > 0
      })
      let transformedAddons = []
      addons.forEach(function (addon) {
        transformedAddons.push({
          id: addon.id,
          amount: parseFloat(addon.sum)
        })
        addonPayments.push({
          id: addon.id,
          amount: parseFloat(addon.sum),
          payment_date: new Date().toJSON().replace(/-/g, '/')
        })
      })
      projectService.postPaymentParticipant(this.project.data.id,
        { 'user_id': this.participants.payment.payment.id,
          'refund': this.participants.payment.refund,
          'notify': this.participants.payment.notify,
          'items': transformedItems,
          'addons': transformedAddons,
          'description': this.participants.payment.paymentDescription })
        .then(() => {
          this.$store.commit('success', 'success.payment')
          transformedItems.forEach(function (transformedItem) {
            let participant = self.participants.data.filter(function (participant) {
              return participant.id === self.participants.payment.payment.id
            })[0]
            let item = participant.items.filter(function (item) {
              return item.id === transformedItem.id
            })[0]
            let itemPayment = itemPayments.filter(function (itemPayment) {
              return item.id === itemPayment.id
            })[0]
            if (self.participants.payment.refund) {
              item.paid -= itemPayment.amount
            } else {
              item.paid += itemPayment.amount
            }
            item.paid = parseFloat(parseFloat(item.paid).toFixed(2))
            if (!item.payments) {
              item.payments = []
            }
            item.payments.push(itemPayment)
          })
          transformedAddons.forEach(function (transformedAddon) {
            let participant = self.participants.data.filter(function (participant) {
              return participant.id === self.participants.payment.payment.id
            })[0]
            let addon = participant.addons.filter(function (addon) {
              return addon.id === transformedAddon.id
            })[0]
            let addonPayment = addonPayments.filter(function (addonPayment) {
              return addon.id === addonPayment.id
            })[0]
            if (self.participants.payment.refund) {
              addon.paid -= addonPayment.amount
            } else {
              addon.paid += addonPayment.amount
            }
            addon.paid = parseFloat(parseFloat(addon.paid).toFixed(2))
            if (!addon.payments) {
              addon.payments = []
            }
            addon.payments.push(addonPayment)
          })
          this.clearPaymentParticipant()
        }, () => {
          this.$store.commit('error', 'errors.payment')
          this.sendNotification('payment', 'error')
        })
    },
    qrCode: function (paymentInfo) {
      return 'BCD\n001\n1\nSCT\n' + paymentInfo.extra_info + '\n' + paymentInfo.name + '\n' + paymentInfo.description + '\nEUR' + this.totalSumToBePaid + '\n\n\n' + this.projectName + ' - ' + this.$store.state.userName
    },
    payPalMe: function (paymentInfo) {
      return 'https://www.paypal.me/' + paymentInfo.extra_info + '/EUR' + this.totalSumToBePaid
    },
    isOwnPickupPoint: function (pickupPoint) {
      return parseInt(pickupPoint.user_id) === parseInt(this.$store.state.userId)
    },
    isOwnPickupPointParticipant: function (pickupPoint, userId) {
      return parseInt(pickupPoint.user_id) === parseInt(userId)
    },
    clearPickupPoint: function () {
      this.participants.pickup.pickupPointId = null
      this.participants.pickup.pickupPointCity = null
      this.participants.pickup.pickupPointCountry = null
      this.participants.pickup.pickupPointLat = null
      this.participants.pickup.pickupPointLng = null
      this.participants.pickup.pickupPointParticipant = null
      this.participants.pickup.pickupPointExtraInfo = null
    },
    initializePickupPoint: function (pickupPoint) {
      if (pickupPoint !== null) {
        this.$v.participants.pickup.$reset()
        this.participants.pickup.pickupPointId = pickupPoint.id
        this.participants.pickup.pickupPointCity = pickupPoint.city
        this.participants.pickup.pickupPointCountry = pickupPoint.country
        this.participants.pickup.pickupPointLat = pickupPoint.lat
        this.participants.pickup.pickupPointLng = pickupPoint.lng
        this.participants.pickup.pickupPointParticipant = this.pickupPointParticipants.filter(function (participant) {
          return participant.id === pickupPoint.user_id
        })[0]
        this.participants.pickup.pickupPointExtraInfo = pickupPoint.extra_info
      } else {
        this.$store.commit('clearUpAutocomplete')
        this.$v.participants.pickup.$reset()
        this.clearPickupPoint()
        this.participants.pickup.pickupPointParticipant = ''
      }
    },
    savePickupPoint: function () {
      projectService.postPickupPoint(this.project.data.id,
        { 'id': this.participants.pickup.pickupPointId,
          'user_id': this.participants.pickup.pickupPointParticipant.id,
          'user_full_name': this.participants.pickup.pickupPointParticipant.full_name,
          'lat': this.participants.pickup.pickupPointLat,
          'lng': this.participants.pickup.pickupPointLng,
          'city': this.participants.pickup.pickupPointCity,
          'country': this.participants.pickup.pickupPointCountry,
          'extra_info': this.participants.pickup.pickupPointExtraInfo })
        .then(() => {
          if (this.participants.pickup.pickupPointId) {
            this.$store.commit('success', 'success.pickup_point_edit')
          } else {
            this.$store.commit('success', 'success.pickup_point_add')
          }
          this.clearPickupPoint()
          this.fetchPickupPoints(this.project.data.id)
          this.fetchDistribution(this.project.data.id)
          this.fetchDistributionLoose(this.project.data.id)
        }, () => {
          if (this.participants.pickup.pickupPointId) {
            this.$store.commit('error', 'errors.pickup_point_edit')
          } else {
            this.$store.commit('error', 'errors.pickup_point_add')
          }
          this.sendNotification('pickupPoint', 'error')
        })
    },
    deletePickupPoint: function (pickupPoint) {
      if (!this.participants.pickup.pickupPointDeleting && !this.project.distribution_locked) {
        this.participants.pickup.pickupPointDeleting = true
        projectService.deletePickupPoint(pickupPoint.id)
          .then(() => {
            this.fetchPickupPoints(this.project.data.id)
            this.fetchDistribution(this.project.data.id)
            this.fetchDistributionLoose(this.project.data.id)
            this.$store.commit('success', 'success.pickup_point_delete')
            this.participants.pickup.pickupPointDeleting = false
          }, () => {
            this.$store.commit('error', 'errors.pickup_point_delete')
            this.participants.pickup.pickupPointDeleting = false
          })
      }
    },
    deliver: function (user) {
      if (user.id === this.project.data.user_id) {
        projectService.delivered(this.project.data.id)
          .then(() => {
            this.project.data.delivered = true
            projectService.userDelivered(this.project.data.id,
              {'user_id': user.id})
              .then(() => {
                this.$store.commit('success', 'success.user_delivered')
                user.delivered = true
                user.loading = false
                if (user.pickups && user.pickups.length > 0) {
                  user.pickups.forEach(function (pickup) {
                    if (pickup.city && pickup.country) {
                      pickup.delivered = true
                    }
                  })
                }
                this.distribution.update += 1
              }, () => {
                user.loading = false
                this.distribution.update += 1
                this.$store.commit('error', 'errors.user_delivered')
              })
          }, () => {
            user.loading = false
            this.distribution.update += 1
            this.$store.commit('error', 'errors.user_delivered')
          })
      } else {
        projectService.userDelivered(this.project.data.id,
          {'user_id': user.id})
          .then(() => {
            this.$store.commit('success', 'success.user_delivered')
            user.delivered = true
            user.loading = false
            if (user.pickups && user.pickups.length > 0) {
              user.pickups.forEach(function (pickup) {
                if (pickup.city && pickup.country) {
                  pickup.delivered = true
                }
              })
            }
            this.distribution.update += 1
          }, () => {
            user.loading = false
            this.distribution.update += 1
            this.$store.commit('error', 'errors.user_delivered')
          })
      }
    },
    ship: function (user) {
      projectService.userDelivered(this.project.data.id,
        { 'user_id': user.id,
          'shipped': true,
          'tracking': user.tracking !== null && user.tracking !== undefined && user.tracking !== '' ? user.tracking : null })
        .then(() => {
          this.$store.commit('success', 'success.user_shipped')
          user.shipped = true
          user.loading = false
          if (user.pickups && user.pickups.length > 0) {
            user.pickups.forEach(function (pickup) {
              if (pickup.city && pickup.country) {
                pickup.shipped = true
              }
            })
          }
          this.distribution.update += 1
        }, () => {
          user.loading = false
          this.distribution.update += 1
          this.$store.commit('error', 'errors.user_shipped')
        })
    },
    pickupParticipant: function (user) {
      user.loadingPickup = true
      this.distribution.update += 1
      this.pickup(user)
    },
    pickup: function (user) {
      projectService.updatePickup(this.project.data.id, { 'id': user.pickup.id, 'type': user.pickup.type, 'user_id': user.id })
        .then(() => {
          user.loadingPickup = false
          this.distribution.update += 1
          this.$store.commit('success', 'success.pickup_change')
          this.fetchDistribution(this.project.data.id)
          this.fetchDistributionLoose(this.project.data.id)
        }, () => {
          user.loadingPickup = false
          this.distribution.update += 1
          this.$store.commit('error', 'errors.pickup_change')
        })
    },
    allowShipping: function () {
      let store = this.$store

      projectService.allowShipping(this.project.data.id)
        .then(() => {
          store.commit('success', 'success.shipping_allowed')
          this.project.data.allow_shipping = true
        }, () => {
          store.commit('error', 'errors.shipping_allowed')
        })
    },
    disallowShipping: function () {
      let store = this.$store

      projectService.disallowShipping(this.project.data.id)
        .then(() => {
          store.commit('success', 'success.shipping_disallowed')
          this.project.data.allow_shipping = false
        }, () => {
          store.commit('error', 'errors.shipping_disallowed')
        })
    },
    lockDistribution: function () {
      let store = this.$store

      projectService.lockDistribution(this.project.data.id)
        .then(() => {
          store.commit('success', 'success.distribution_locked')
          this.project.data.distribution_locked = true
        }, () => {
          store.commit('error', 'errors.distribution_locked')
        })
    },
    isEqualProducts: function (ownProducts, userProducts) {
      if (ownProducts && userProducts) {
        let equals = ownProducts.length === userProducts.length
        ownProducts.forEach(function (ownProduct) {
          if (equals) {
            let userProductList = userProducts.filter(function (userProduct) {
              return ownProduct.id === userProduct.id
            })
            if (userProductList.length === 1) {
              equals = ownProduct.amount === userProductList[0].amount
            } else {
              equals = false
            }
          }
        })
        return equals
      } else {
        return false
      }
    },
    initializeMap: _.debounce(function () {
      if (this.participants.pickupPoints && this.participants.pickupPoints.length > 0 && !this.map.initialized) {
        let container = document.getElementById('projectMap')
        if (container) {
          let self = this
          // eslint-disable-next-line no-undef
          let map = new L.Map('projectMap', {
            fullscreenControl: true,
            center: [0, 0],
            zoom: 10
          })
          let markers = []
          // eslint-disable-next-line no-undef
          let participantIcon = L.icon({
            iconUrl: this.participantIcon,
            iconSize: [25, 30]
          })
          if (this.participants.data) {
            this.participants.data.forEach(function (participant) {
              // eslint-disable-next-line no-undef
              let marker = new L.Marker([participant.lat, participant.lng], {
                icon: participantIcon,
                title: participant.city + ', ' + self.$i18n.t('countries.' + participant.country)
              })
              let content = '<p>' + participant.city + ', ' + self.$i18n.t('countries.' + participant.country) + '</p>'
              if (self.loggedIn) {
                content = '<p style="font-size: 16px; font-weight: bold;"><a href="/user/' + self.slugText(participant.id + '-' + participant.full_name) + '">' + participant.full_name + '</a></p>' + content
              }
              marker.bindPopup(content)
              marker.addTo(map)
              markers.push(marker)
            })
          }
          // eslint-disable-next-line no-undef
          let pickupPointIcon = L.icon({
            iconUrl: this.pickupPointIcon,
            iconSize: [25, 30]
          })
          this.participants.pickupPoints.forEach(function (pickupPoint) {
            // eslint-disable-next-line no-undef
            let marker = new L.Marker([pickupPoint.lat, pickupPoint.lng], {
              icon: pickupPointIcon,
              title: pickupPoint.city + ', ' + self.$i18n.t('countries.' + pickupPoint.country) + (pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '')
            })
            let content = '<p>' + pickupPoint.city + ', ' + self.$i18n.t('countries.' + pickupPoint.country) + (pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '') + '</p>'
            if (self.loggedIn) {
              content = '<p style="font-size: 16px; font-weight: bold;"><a href="/user/' + self.slugText(pickupPoint.user_id + '-' + pickupPoint.full_name) + '">' + pickupPoint.full_name + '</a></p>' + content
            }
            marker.bindPopup(content)
            marker.addTo(map)
            markers.push(marker)
          })
          // eslint-disable-next-line no-undef
          let group = new L.FeatureGroup(markers)
          map.fitBounds(group.getBounds(), {
            padding: [50, 50]
          })
          // eslint-disable-next-line no-undef, no-template-curly-in-string
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }).addTo(map)
          this.map.initialized = true
        }
      }
    }, 1000),
    setCityAndCountry: function (city, country, lat, lng) {
      this.$set(this.participants.pickup, 'pickupPointCity', city)
      this.$set(this.participants.pickup, 'pickupPointCountry', country)
      this.$set(this.participants.pickup, 'pickupPointLat', lat)
      this.$set(this.participants.pickup, 'pickupPointLng', lng)
      this.$v.participants.pickup.pickupPointCity.$touch()
      this.$v.participants.pickup.pickupPointCountry.$touch()
      this.$v.participants.pickup.pickupPointLat.$touch()
      this.$v.participants.pickup.pickupPointLng.$touch()
    },
    getCost: function (price, shipping, shippingKnown) {
      let text = '€ '.concat(parseFloat(price).toFixed(2)).concat(' ').concat(this.$i18n.t('messages.with')).concat(' ')
      if (shippingKnown) {
        if (shipping > 0) {
          text = text.concat('€ ').concat(parseFloat(shipping).toFixed(2)).concat(' ').concat(this.$i18n.t('messages.shipping_info'))
        } else {
          text = text.concat(this.$i18n.t('messages.shipping_free'))
        }
      } else {
        text = text.concat(this.$i18n.t('messages.shipping_unknown'))
      }
      return text
    },
    startEditing (editing) {
      let self = this
      let loader = this.$loading.show()
      setTimeout(function () {
        self.editing = editing
        self.participants.payment.scroll = null
        self.participants.payment.scrollUpdate = false
        if (self.editing) {
          self.fetchParticipants(self.project.data.id)
          self.fetchDistribution(self.project.data.id)
          self.fetchDistributionLoose(self.project.data.id)
          loader.hide()
        } else {
          self.fetchItems(self.project.data.id)
          self.fetchAddons(self.project.data.id)
          self.fetchDistribution(self.project.data.id)
          self.fetchDistributionLoose(self.project.data.id)
          self.map.initialized = false
          self.initializeMap()
          loader.hide()
        }
      }, 100)
    }
  },
  watch: {
    'project.data': function () {
      this.initializePickupOptions()
    },
    'items.data': function () {
      this.initializePickupOptions()
    },
    'participants.data': function () {
      this.initializeMap()
      this.initializePickupOptions()
    },
    'participants.pickupPoints': function () {
      this.initializeMap()
      this.initializePickupOptions()
    },
    'participants.payment.refundType': function () {
      this.participants.payment.paymentDescription = this.participants.payment.refundType.message
    },
    'items.selectedTemp': function () {
      this.items.selected = this.items.selectedTemp.filter(function (selectedItem) {
        return selectedItem !== false && selectedItem !== null
      })
    },
    'items.normalShippingKnown': function () {
      if (!this.items.normalShippingKnown) {
        this.items.normalShipping = 0.00
        this.$v.items.normalShipping.$reset()
      }
    },
    'items.newShippingKnown': function () {
      if (!this.items.newShippingKnown) {
        this.items.newShipping = 0.00
        this.$v.items.newShipping.$reset()
      }
    },
    'addons.shippingKnown': function () {
      if (!this.addons.shippingKnown) {
        this.addons.shipping = 0.00
        this.$v.addons.shipping.$reset()
      }
    },
    'addons.selectedTemp': function () {
      this.addons.selected = this.addons.selectedTemp.filter(function (selectedAddon) {
        return selectedAddon !== false && selectedAddon !== null
      })
    }
  },
  created: function () {
    let self = this

    this.fetchProject()
    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
    document.addEventListener('keydown', function (e) {
      if (e.keyCode === 27 || e.key === 'Escape') {
        self.distribution.show = false
        self.distribution.showShipping = false
      }
    })
  },
  mounted: function () {
    let self = this
    window.addEventListener('resize', function () {
      self.map.initialized = false
      if (self.map.timeout) {
        clearTimeout(self.map.timeout)
      }
      self.map.timeout = setTimeout(function () {
        self.initializeMap()
      }, 1000)
    })
    this.comments.interval = setInterval(function () {
      if (self.project.data && !self.editing) {
        self.fetchComments(self.project.data.id)
      }
    }, 5000)
  },
  updated: function () {
    let paymentScroll = document.getElementById('paymentScroll')
    if (paymentScroll && this.participants.payment.scrollUpdate && !!this.participants.payment.scroll) {
      paymentScroll.scrollTop = this.participants.payment.scroll
      this.participants.payment.scrollUpdate = false
    }
    if (this.participants.payment.paymentUpdate) {
      if (this.$refs.participantPaymentAmount) {
        this.$refs.participantPaymentAmount.$el.focus()
      }
      this.participants.payment.paymentUpdate = false
    }
  },
  beforeDestroy: function () {
    this.removeCommentPuller()
    this.$root.$off('updateLocale')
    clearTimeout(this.map.timeout)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .masonry {
    padding: 10px;
  }

  .card__info {
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__item, .card__addon {
    border-bottom: 1px #D3D3D3 solid;
    height: 100%;
    width: 100%;
    padding-right: 70px;
  }

  .card__item input, .card__addon input {
    margin: -6px 0px 19px 50px;
  }

  .card__payment__border__bottom, .card__participant__border__bottom, .card__participant__border__bottom-responsive, .card__item__border__bottom, .card__addon__border__bottom, .card__pickup__point__border__bottom {
    border-bottom: 1px #D3D3D3 solid;
  }

  @media screen and (min-width: 901px) {
    .card__participant__border__bottom-responsive {
      border-bottom: none;
    }

    .card__participant__wrapper, .card__item__wrapper, .card__addon__wrapper, .card__pickup__point__wrapper {
      display: -webkit-flex; /* Safari */
      display: flex;
      width: 100%;
    }

    .card__participant__wrapper__part, .card__item__wrapper__part, .card__addon__wrapper__part, .card__pickup__point__wrapper__part {
      -webkit-flex-grow: 1; /* Safari */
      flex-grow: 1;
    }

    .card__item, .card__addon {
      width: 125px;
      border-right: 1px #D3D3D3 solid;
      margin-top: 0px;
      padding-top: 5px;
    }

    .card__item input, .card__addon input {
      width: 55px;
    }

    .card__addon__part, .card__addon__part__info {
      height: 83px;
      display: -webkit-flex; /* Safari */
      display: flex;
      -webkit-align-items: center; /* Safari */
      align-items: center;
    }

    .card__addon__part__info {
      justify-content: center;
    }
  }

  .card__payment__info__wrapper, .card__item__part__wrapper, .card__addon__part__wrapper, .card__participant__part__wrapper, .card__pickup__point__part__wrapper {
    display: -webkit-flex; /* Safari */
    display: flex;
    width: 100%;
  }

  .card__payment__info__wrapper {
    padding: 0px 20px;
  }

  .card__payment__info__icon {
    display: -webkit-flex; /* Safari */
    display: flex;
    -webkit-align-items: center; /* Safari */
    align-items: center;
    -webkit-justify-content: center;
    justify-content: center;
    min-width: 35px;
  }

  .card__payment__code {
    min-width: 100px;
    text-align: center;
  }

  .card__payment__info__part, .card__item__participant__part, .card__addon__participant__part, .card__item__part, .card__addon__part, .card__pickup__point__part, .card__participant__part {
    padding: 10px 15px;
    -webkit-flex-grow: 1; /* Safari */
    flex-grow: 1;
    word-break: break-word;
  }

  .card__payment__info__part {
    padding: 10px 20px 10px 15px;
  }

  .card__item__participant__part__manage, .card__addon__participant__part__manage, .card__item__part__manage, .card__addon__part__manage, .card__pickup__point__part__manage, .card__participant__part__manage {
    padding: 10px 20px;
  }

  .card__item__part__info, .card__addon__part__info, .card__pickup__point__part__info, .card__item__part__order__info, .card__addon__part__order__info {
    padding: 10px 15px;
    border-left: 1px #D3D3D3 solid;
    font-weight: bold;
    min-width: 110px;
    max-width: 110px;
    text-align: center;
  }

  .info_button_margin {
    margin-left: 5px;
    margin-top: -1px;
  }

  .card__pickup__point__part__info {
    min-width: 150px;
    max-width: 150px;
  }

  .card__item__part__order__info, .card__addon__part__order__info {
    min-width: 80px;
    max-width: 80px;
  }

  .card__participant__payment__part {
    -webkit-flex-grow: 1; /* Safari */
    flex-grow: 1;
    text-align: center;
    padding: 10px;
    width: 100px;
  }

  .card__pickup__send, .card__comment__item {
    padding: 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .manage_width {
    min-width: 65px;
  }

  .fix-shipping-checkbox {
    margin-top: -20px;
    margin-bottom: 20px;
  }

  .showDistribution {
    display: block !important;
    overflow: scroll;
    background: #ffffff;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0px;
    top: 0px;
    z-index: 1000;
    padding-top: 40px;
  }

  .showDistribution .showDistributionClose {
    position: fixed;
    top: 20px;
    right: 30px;
    z-index: 1001;
    background: transparent;
    border: none;
    padding: 0px;
    cursor: pointer;
  }

  .fa-disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .no-decoration {
    text-decoration: none;
  }

  .bg-odd {
    background-color: rgba(0, 0, 0, 0.05) !important;
  }

  #projectMap {
    margin-top: 15px;
    width: 100%;
    height: 300px;
  }
</style>



// WEBPACK FOOTER //
// src/pages/project/Project.vue